import {
    ChangeEvent,
    CSSProperties,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {HistoryContext} from '../../contexts/history'
import {AuthContext} from '../../contexts/auth'
import {useBuscaFornecedor} from './useBuscaFornecedor'
import AiDisclaimer from '../../globalComponents/AiDisclaimer'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    MenuItem,
    Modal,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {getCurrentColors} from '../../utils/colors'
import {cpfValido, fmtDataHora, formatCnpj, formatCpf, formatCpfCnpj, hojeBR} from './format'
import {RadarFornecedorApiService} from '../../services/RadarFornecedorApi.service'
import {AssistantApi} from '../../services/AssistantApi.service'
import {
    AutuacaoAmbiental,
    Certidao,
    DadosPfCertidoes,
    Protesto,
    ConsultaDetail,
    ConsultaOut,
    ContratacaoFornecedor,
    FornecedorCandidato,
    MencaoMidia,
    MencaoOficial,
    RadarFornecedor,
    Sancao,
    SinalIrregularidade,
    Socio,
    Veredito,
    VinculoServidor,
} from '../../types/radarFornecedor'
import {
    CERTIDAO_NOME,
    MICROCOPY_SENSIVEL,
    alternarSelecaoAcoplada,
    alternarTodas,
    comImplicitos,
    reciboExpirado,
    camposPf,
    ehEmitivel,
    ehPendente,
    ehInscricao,
    ehSensivel,
    faltamDadosPf,
    montarDadosPf,
    motivoCampoPf,
    podeEmitir,
    rotuloEmitir,
    selecaoTravada,
    todasSelecionadas,
    uiCertidao,
} from './certidoesUi'

const api = new RadarFornecedorApiService()
// Ficha detalhada: 1 carga inicial e o andamento chega por PUSH do notifier
// (websocket). Mesmo padrão do analisador (R.E.D), do gerador e da pesquisa de preços —
// diretriz do CTO de parar o polling agressivo no backend.
const STATUS_FINAIS = ['CONCLUIDO', 'ERRO']
const POR_PAGINA = 15

// Emissão de certidões SOB DEMANDA é lenta: cada certidão é uma consulta AO VIVO no órgão,
// e um lote de ~9 certidões pode levar alguns minutos. Enquanto o lote está CONSULTANDO,
// sondamos a ficha (o MESMO GET que já popula as certidões) num intervalo brando até o
// status virar terminal — bounded e SÓ durante a emissão (não é o polling agressivo que o
// CTO baniu). O push do notifier segue acelerando quando chega; o polling é a rede que faz
// o "Emitindo…" sobreviver à navegação e garante que a ficha se atualize sozinha ao concluir.
const CERTIDOES_POLL_MS = 6000
const CERTIDOES_POLL_TETO_MS = 5 * 60 * 1000

const COLORS = getCurrentColors()

function darken(hex: string, f: number): string {
    const m = (hex || '').replace('#', '').match(/.{2}/g)
    if (!m || m.length !== 3) return '#0b7d6b'
    const v = m.map((x) => Math.max(0, Math.round(parseInt(x, 16) * (1 - f))))
    return '#' + v.map((x) => x.toString(16).padStart(2, '0')).join('')
}

// Identidade visual da marca (teal/verde). Adapta a white-label via getCurrentColors.
const VERDE = COLORS.secundariaEscura || '#3acdb4'
const VERDE_ESCURO = darken(VERDE, 0.45)

const VEREDITO_UI: Record<Veredito, {label: string; cor: string; fundo: string; emoji: string}> = {
    APTO: {label: 'Regular', cor: '#15803d', fundo: '#dcfce7', emoji: '🟢'},
    ATENCAO: {label: 'Atenção', cor: '#b45309', fundo: '#fef3c7', emoji: '🟡'},
    IMPEDIDO: {label: 'Impedimento identificado', cor: '#b91c1c', fundo: '#fee2e2', emoji: '🔴'},
    INDEFINIDO: {label: 'Indefinido', cor: '#475569', fundo: '#f1f5f9', emoji: '⚪'},
}

const NIVEL_COR: Record<string, string> = {ALTO: '#b91c1c', MEDIO: '#b45309', BAIXO: '#475569'}

const FONTE_SANCAO_LABEL: Record<string, string> = {
    CEIS: 'CEIS',
    CNEP: 'CNEP',
    CEPIM: 'CEPIM',
    LENIENCIA: 'Acordo de leniência (CGU)',
    TCU_INIDONEOS: 'Inidôneos do TCU',
    LISTA_SUJA: 'Trabalho análogo a escravo (MTE)',
}

const SENT_UI: Record<string, {emoji: string; cor: string; rotulo: string}> = {
    NEGATIVO: {emoji: '🔻', cor: '#b91c1c', rotulo: 'tom negativo'},
    POSITIVO: {emoji: '🔺', cor: '#15803d', rotulo: 'tom positivo'},
    NEUTRO: {emoji: '•', cor: '#64748b', rotulo: 'tom neutro'},
}

const TIPS = [
    {
        emoji: '⚖️',
        cor: '#b91c1c',
        titulo: 'Nem toda sanção impede contratar',
        texto: 'A inidoneidade (art. 156, IV) impede com toda a Administração; a suspensão restringe-se ao órgão sancionador (TCU, Acórdão 2243/2013-Plenário). Verifique o tipo.',
    },
    {
        emoji: '🔎',
        cor: VERDE_ESCURO,
        titulo: 'Diligência prévia é dever do gestor',
        texto: 'O TCU entende que cabe à Administração verificar a idoneidade do fornecedor antes de contratar, sob pena de responsabilização do gestor.',
    },
    {
        emoji: '🧾',
        cor: '#1d4ed8',
        titulo: 'Conflito de interesse é vedação legal',
        texto: 'O art. 14 da Lei 14.133 veda a participação de empresa cujo dirigente seja agente público do órgão contratante.',
    },
    {
        emoji: '🆕',
        cor: '#7c3aed',
        titulo: 'Empresa recém-criada pede atenção',
        texto: 'CNPJ aberto há pouco tempo vencendo contratos relevantes merece checagem de capacidade técnica e experiência — um ponto de atenção, não uma irregularidade.',
    },
    {
        emoji: '🚫',
        cor: '#be123c',
        titulo: 'CNPJ irregular na Receita',
        texto: 'Empresa baixada, inapta ou suspensa na Receita Federal não tem aptidão para ser contratada.',
    },
    {
        emoji: '💸',
        cor: '#0f766e',
        titulo: 'Sanções de até 6 anos',
        texto: 'As penalidades da Lei 14.133 vão de advertência à declaração de inidoneidade, por até 6 anos (art. 156).',
    },
    {
        emoji: '📊',
        cor: '#0f766e',
        titulo: 'Preço de referência é obrigatório',
        texto: 'A pesquisa de preços (art. 23 da Lei 14.133 e IN SEGES 65/2021) deve usar pelo menos 3 fontes e tratar os preços fora da curva antes de definir o valor estimado.',
    },
    {
        emoji: '📄',
        cor: '#1d4ed8',
        titulo: 'Habilitação tem documentação própria',
        texto: 'Regularidade fiscal (CND, FGTS), trabalhista (CNDT), qualificação técnica e econômico-financeira são exigências dos arts. 62 a 70 da Lei 14.133.',
    },
    {
        emoji: '🤝',
        cor: '#7c3aed',
        titulo: 'ME e EPP têm tratamento favorecido',
        texto: 'A LC 123/2006 garante preferência e regularização posterior de documentos fiscais a microempresas e empresas de pequeno porte.',
    },
    {
        emoji: '🗂️',
        cor: VERDE_ESCURO,
        titulo: 'Planejamento vem antes',
        texto: 'ETP e Termo de Referência estruturam a contratação: descrevem a necessidade, a solução e os requisitos antes de ir ao mercado (art. 18 da Lei 14.133).',
    },
    {
        emoji: '🏛️',
        cor: '#b45309',
        titulo: 'Concentração não é, por si só, irregularidade',
        texto: 'Vencer muito em um único órgão pode ser legítimo — é um ponto de atenção que merece olhar para a competitividade do certame, não uma acusação.',
    },
]

// formatCnpj/formatCpf/formatCpfCnpj/cpfValido moram em ./format (puros e testáveis).

// A ficha é de pessoa física (CPF)? Fichas ANTIGAS não têm tipo_documento → CNPJ (compat).
function fichaEhCpf(r: {tipo_documento?: 'CPF' | 'CNPJ'}): boolean {
    return (r.tipo_documento ?? 'CNPJ') === 'CPF'
}

function brl(v?: number | null): string {
    if (typeof v !== 'number') return '—'
    return v.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
}

function fmtData(d?: string | null): string {
    if (!d || d.length < 10) return d || '—'
    const [a, m, dia] = d.slice(0, 10).split('-')
    return `${dia}/${m}/${a}`
}

// Valida o dígito verificador do CNPJ (ignora pontuação). Só é chamado quando a entrada
// parece um CNPJ — nome continua indo para a busca por nome.
function cnpjValido(v: string): boolean {
    const d = (v || '').replace(/\D/g, '')
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false
    const dv = (base: string) => {
        let soma = 0
        let peso = base.length - 7
        for (const ch of base) {
            soma += parseInt(ch, 10) * peso
            peso = peso === 2 ? 9 : peso - 1
        }
        const r = soma % 11
        return r < 2 ? 0 : 11 - r
    }
    const d1 = dv(d.slice(0, 12))
    return d1 === +d[12] && dv(d.slice(0, 12) + d1) === +d[13]
}

const LINK_RECEITA =
    'https://solucoes.receita.fazenda.gov.br/Servicos/cnpjreva/Cnpjreva_Solicitacao.asp'
// Consulta PÚBLICA do Certificado de Registro Cadastral (CRC) do SICAF: é onde aparecem os
// níveis de cadastro e as respectivas VALIDADES — a API de dados abertos não devolve
// vigência. Não exige login gov.br; pede captcha (por isso só linkamos, não raspamos).
const LINK_SICAF_CRC =
    'https://www3.comprasnet.gov.br/sicaf-web/public/pages/consultas/consultarCRC.jsf'
const linkTransparencia = (cnpj: string) =>
    `https://portaldatransparencia.gov.br/busca?termo=${cnpj}`
// Conferência manual dos diários (cobertura do robô é parcial): busca pelo nome.
const buscaQueridoDiario = (termo: string) =>
    `https://queridodiario.ok.org.br/pesquisa?termo=${encodeURIComponent(termo)}`
const buscaDOU = (termo: string) =>
    `https://www.in.gov.br/consulta/-/buscar/dou?q=${encodeURIComponent('"' + termo + '"')}`
// TCU não tem API aberta gratuita: link para a consulta oficial em tempo real.
const LINK_TCU =
    'https://portal.tcu.gov.br/carta-de-servicos/certidoes/lista-de-licitantes-inidoneos'

// Certidões obrigatórias na habilitação (arts. 62 a 68, Lei 14.133). Usado como fallback
// (consultas antigas, sem o bloco `certidoes` no resultado): link direto aos portais
// oficiais para o gestor emitir/conferir. `somentePj` marca as que NÃO existem para
// pessoa física (ficha CPF filtra o fallback).
const CERTIDOES: {nome: string; desc: string; url: string; somentePj?: boolean}[] = [
    {
        nome: 'CND Federal (Receita/PGFN)',
        desc: 'Tributos federais e dívida ativa da União',
        // 14/08/2026: a página do catálogo gov.br que estava aqui
        // (…-perante-a-fazenda-nacional) segue no ar, mas os três canais que ela
        // publica (PF/PJ/ITR em solucoes.receita.fazenda.gov.br/…/certidaointernet)
        // respondem 404 — a Receita migrou a emissão para o Portal de Serviços e não
        // atualizou o catálogo. Este é o destino do botão "Iniciar" da página oficial
        // vigente do serviço; mesmo endereço usado pelo price-research em
        // LINKS_EMISSAO_MANUAL, para o gestor não cair em link morto pelo fallback.
        url: 'https://servicos.receitafederal.gov.br/servico/certidoes/#/home',
    },
    {
        nome: 'FGTS — CRF (Caixa)',
        desc: 'Regularidade do FGTS',
        url: 'https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf',
        // O CRF é do EMPREGADOR (CNPJ) — não se aplica à consulta de CPF.
        somentePj: true,
    },
    {
        nome: 'CNDT (TST)',
        desc: 'Débitos trabalhistas',
        url: 'https://cndt-certidao.tst.jus.br/inicio.faces',
    },
    {
        nome: 'Consulta Consolidada TCU',
        desc: 'Inidôneos TCU, improbidade (CNJ), CEIS e CNEP',
        url: 'https://certidoes-apf.apps.tcu.gov.br/',
    },
]

const card: CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: 22,
    background: '#fff',
    marginBottom: 18,
    boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
}

const linkStyle: CSSProperties = {color: VERDE_ESCURO, fontWeight: 600, textDecoration: 'none'}

export default function RadarPanel() {
    const [fase, setFase] = useState<'inicio' | 'escolha' | 'relatorio'>('inicio')
    const [origem, setOrigem] = useState<'inicio' | 'escolha'>('inicio')
    const [termo, setTermo] = useState('')
    const [detalhe, setDetalhe] = useState<ConsultaDetail | null>(null)
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState<string | null>(null)
    const [exportando, setExportando] = useState(false)
    const [recentes, setRecentes] = useState<ConsultaOut[]>([])
    const [feedbackModal, setFeedbackModal] = useState(false)
    const [feedbackText, setFeedbackText] = useState('')
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
    const [feedbackToast, setFeedbackToast] = useState<{
        open: boolean
        message: string
        severity: 'success' | 'error'
    }>({open: false, message: '', severity: 'success'})
    // Push do notifier (websocket) acelera o progresso; pendenteRef = consulta aguardada.
    const {radarSocketMessage, wsAberto, wsReconexoes} = useContext(HistoryContext)
    const {userInfo} = useContext(AuthContext)
    const buscaNome = useBuscaFornecedor({
        api, userId: userInfo.guid, mensagem: radarSocketMessage,
        wsAberto, wsReconexoes,
    })
    useEffect(() => {
        if (!buscaNome.recuperada) return
        setTermo(buscaNome.recuperada.termo)
        setFase('escolha')
    }, [buscaNome.recuperada])
    const pendenteRef = useRef<string | null>(null)
    // Tipos do lote de certidões em emissão AGORA (sessão viva) → deixa o "Emitindo…" preciso
    // por linha. null = não sabemos quais (ficha reaberta): aí toda linha ainda não emitida
    // aparece como "Emitindo…" enquanto o status for CONSULTANDO.
    const [emitindoTipos, setEmitindoTipos] = useState<string[] | null>(null)

    const carregarRecentes = useCallback(async () => {
        try {
            const r = await api.listarConsultas(8, 0)
            setRecentes(r.consultas || [])
        } catch {
            /* best-effort: recentes não bloqueia a tela */
        }
    }, [])

    useEffect(() => {
        carregarRecentes()
    }, [carregarRecentes])

    // Carga (1 request): busca a ficha atual da consulta. Sem repetição — quem avisa de
    // mudança é o push do notifier (useEffect abaixo).
    const carregarDetalhe = async (id: string) => {
        try {
            const d = await api.getConsulta(id)
            setDetalhe(d)
            if (STATUS_FINAIS.includes(d.status)) setCarregando(false)
            // Certidões sob demanda em andamento (ficha reaberta/2ª aba/F5): arma o push
            // — sem isso o CONCLUIDO/ERRO das certidões seria descartado (spinner eterno).
            if (d.resultado?.certidoes_status === 'CONSULTANDO') pendenteRef.current = id
        } catch {
            setCarregando(false)
            setErro('Falha ao consultar o andamento.')
        }
    }

    // PUSH do notifier: chegou o status final da consulta que aguardamos → recarrega a ficha
    // na hora. Push puro, sem polling — mesmo padrão do analisador, gerador e pesquisa de preços.
    useEffect(() => {
        const msg = (radarSocketMessage || {}) as {consulta_id?: string; status?: string}
        if (!msg.consulta_id || msg.consulta_id !== pendenteRef.current) return
        if (msg.status === 'CONCLUIDO' || msg.status === 'ERRO') {
            carregarDetalhe(msg.consulta_id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radarSocketMessage])

    const iniciarConsulta = async (cnpj: string, nome?: string, de: 'inicio' | 'escolha' = 'inicio') => {
        if (de === 'inicio') buscaNome.limpar()
        setOrigem(de)
        setErro(null)
        setDetalhe(null)
        setCarregando(true)
        setFase('relatorio')
        try {
            // Período fixo de 5 anos (decisão de produto 23/07/2026): não envia periodo_meses.
            const consulta = await api.criarConsulta(cnpj, nome)
            pendenteRef.current = consulta.id // aguarda o push do notifier p/ esta consulta
            carregarDetalhe(consulta.id) // estado inicial (PENDENTE/COLETANDO)
        } catch (e: any) {
            setCarregando(false)
            setErro(
                e?.response?.data?.detail ||
                    'Não foi possível iniciar a consulta. Verifique se você está logado e se o serviço está no ar.'
            )
        }
    }

    const abrirConsulta = async (id: string) => {
        buscaNome.limpar()
        setOrigem('inicio')
        setErro(null)
        setDetalhe(null)
        setCarregando(true)
        setFase('relatorio')
        try {
            const d = await api.getConsulta(id)
            setDetalhe(d)
            if (STATUS_FINAIS.includes(d.status)) setCarregando(false)
            else pendenteRef.current = id // ainda em andamento: aguarda o push do notifier
            // Certidões sob demanda em andamento nesta ficha → arma o push também (a
            // consulta em si já está CONCLUIDO, mas o lote de certidões não).
            if (d.resultado?.certidoes_status === 'CONSULTANDO') pendenteRef.current = id
        } catch {
            setCarregando(false)
            setErro('Não foi possível abrir a consulta.')
        }
    }

    const buscarNome = (nome: string) => {
        if (!userInfo.guid) {
            setErro('Aguarde o carregamento da sua sessão e tente novamente.')
            return
        }
        setErro(null)
        setFase('escolha')
        void buscaNome.iniciar(nome)
    }

    const consultar = () => {
        const t = termo.trim()
        const digitos = t.replace(/\D/g, '')
        const pareceDocumento = /^[\d.\/\-\s]+$/.test(t) && digitos.length > 0 // só números/pontuação
        if (pareceDocumento) {
            // Campo único CPF/CNPJ: o comprimento decide o documento (11 = CPF de pessoa
            // física, 14 = CNPJ). O backend aceita os dois no mesmo campo `cnpj`.
            if (digitos.length === 11) {
                if (!cpfValido(digitos)) {
                    setErro('CPF inválido — confira os 11 dígitos (com ou sem pontuação).')
                    return
                }
                iniciarConsulta(digitos)
            } else if (digitos.length === 14) {
                if (!cnpjValido(digitos)) {
                    setErro('CNPJ inválido — confira os 14 dígitos (com ou sem pontuação).')
                    return
                }
                iniciarConsulta(digitos)
            } else {
                setErro(
                    'Documento incompleto — informe um CPF (11 dígitos) ou um CNPJ (14 dígitos), com ou sem pontuação.'
                )
            }
        } else if (t.length >= 3) {
            buscarNome(t)
        } else {
            setErro('Digite um CPF, um CNPJ ou o nome da empresa (mín. 3 letras).')
        }
    }

    const novaConsulta = () => {
        pendenteRef.current = null
        setFase('inicio')
        setDetalhe(null)
        buscaNome.limpar()
        setErro(null)
        setTermo('')
        carregarRecentes()
    }

    const voltar = () => {
        setErro(null)
        if (fase === 'relatorio' && origem === 'escolha') {
            setDetalhe(null)
            setFase('escolha')
        } else {
            novaConsulta()
        }
    }

    const radar: RadarFornecedor | null = detalhe?.resultado ?? null

    const exportarPdf = async () => {
        if (!detalhe?.id) return
        setExportando(true)
        try {
            const blob = await api.exportarPdf(detalhe.id)
            baixarArquivo(blob, `radar-${(radar?.razao_social || detalhe.cnpj || 'fornecedor').slice(0, 40)}.pdf`)
        } catch {
            setErro('Não foi possível gerar o PDF agora.')
        } finally {
            setExportando(false)
        }
    }

    // SOB DEMANDA (produto 24/07/2026): dispara as consultas PAGAS de certidões/protestos.
    // 202 + background no backend; o resultado chega pelo MESMO push do notifier (o
    // pendenteRef faz o carregarDetalhe recarregar a ficha ao concluir/errar).
    // `dadosPf` só existe na ficha CPF (emissão PF destravada 27/08/2026): dados do
    // titular que algumas certidões exigem, coletados no mini-form da SecaoCertidoes.
    const solicitarCertidoes = async (tipos: string[], dadosPf?: DadosPfCertidoes) => {
        if (!detalhe?.id || !radar) return
        // Guarda os tipos deste lote p/ o "Emitindo…" por linha (some ao concluir/errar).
        setEmitindoTipos(tipos)
        const armarConsultando = () => {
            pendenteRef.current = detalhe.id
            setDetalhe({
                ...detalhe,
                resultado: {
                    ...radar,
                    certidoes_status: 'CONSULTANDO',
                    // Timestamp otimista: casa com o backend e alimenta o teto do polling
                    // mesmo antes do 1º GET de andamento chegar.
                    certidoes_solicitadas_em: new Date().toISOString(),
                },
            })
        }
        try {
            // Sem `dadosPf` a chamada mantém a aridade antiga (body idêntico ao de CNPJ).
            await (dadosPf
                ? api.solicitarCertidoes(detalhe.id, tipos, dadosPf)
                : api.solicitarCertidoes(detalhe.id, tipos))
        } catch (e: any) {
            if (e?.response?.status === 409) {
                // Outra aba/solicitação já emitindo: NÃO é erro — vira o estado
                // informativo "em andamento" e esta aba também passa a ouvir o push.
                armarConsultando()
                return
            }
            // 429 (cooldown) e demais: mensagem do backend quando houver (pt, pronta).
            throw new Error(
                e?.response?.data?.detail ||
                    'Não foi possível iniciar a emissão — tente novamente.'
            )
        }
        armarConsultando()
    }

    // ANDAMENTO da emissão sob demanda (sobrevive à navegação): enquanto o backend indicar
    // CONSULTANDO, sonda a ficha a cada CERTIDOES_POLL_MS até o status virar terminal
    // (CONCLUIDAS/NAO_SOLICITADAS) — ou até o teto de segurança. Bounded e só durante a
    // emissão; ao reabrir uma ficha já CONSULTANDO, este efeito retoma o polling sozinho.
    const statusCertidoes = detalhe?.resultado?.certidoes_status
    const solicitadasEm = detalhe?.resultado?.certidoes_solicitadas_em
    useEffect(() => {
        if (statusCertidoes !== 'CONSULTANDO' || !detalhe?.id) return
        const id = detalhe.id
        const inicio = Date.now()
        const timer = setInterval(() => {
            // Sem loop infinito: passado o teto, para de sondar (o push ainda pode fechar).
            if (Date.now() - inicio > CERTIDOES_POLL_TETO_MS) {
                clearInterval(timer)
                return
            }
            carregarDetalhe(id)
        }, CERTIDOES_POLL_MS)
        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusCertidoes, solicitadasEm, detalhe?.id])

    // Terminou (ou reverteu) o lote → esquece os tipos "Emitindo…" da sessão viva.
    useEffect(() => {
        if (statusCertidoes !== 'CONSULTANDO') setEmitindoTipos(null)
    }, [statusCertidoes])

    const handleSubmitFeedback = async () => {
        if (!detalhe?.id || !feedbackText.trim()) return

        setFeedbackSubmitting(true)
        try {
            const assistantApi = new AssistantApi()
            await assistantApi.reportModule({
                object_id: detalhe.id,
                module: 'supplier_radar',
                title: radar?.razao_social || detalhe.cnpj || 'Fornecedor',
                feedback: feedbackText,
            })
            setFeedbackToast({
                open: true,
                message: 'Obrigado pelo feedback.',
                severity: 'success',
            })
            setFeedbackModal(false)
            setFeedbackText('')
        } catch (error) {
            console.error('Erro ao enviar feedback:', error)
            setFeedbackToast({
                open: true,
                message: 'Erro ao enviar feedback. Tente novamente.',
                severity: 'error',
            })
        } finally {
            setFeedbackSubmitting(false)
        }
    }

    return (
        <div style={{flex: 1, overflowY: 'auto', background: '#f1f5f9'}}>
            <BarraBusca
                inicio={fase === 'inicio'}
                termo={termo}
                setTermo={setTermo}
                onConsultar={consultar}
            />

            <div style={{maxWidth: 960, margin: '0 auto', padding: '0 24px 40px'}}>
                {fase !== 'inicio' && (
                    <button onClick={voltar} aria-label="Voltar" style={backBtn}>
                        ← Voltar
                    </button>
                )}

                {erro && (
                    <div style={{...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c'}}>
                        {erro}
                    </div>
                )}

                {fase === 'inicio' && <Landing recentes={recentes} onAbrir={abrirConsulta} />}

                {fase === 'escolha' && (
                    <>
                        {buscaNome.erro && <Alert severity="warning" sx={{mb: 2}}>{buscaNome.erro}</Alert>}
                        {buscaNome.podeVerificar && !buscaNome.buscando && (
                            <Button onClick={buscaNome.verificar}>Verificar andamento</Button>
                        )}
                        <Escolha
                            termo={buscaNome.termo || termo}
                            buscando={buscaNome.buscando}
                            buscaFeita={buscaNome.busca?.status === 'CONCLUIDO'}
                            candidatos={buscaNome.busca?.candidatos || []}
                            onEscolher={(c) => {
                                setTermo(formatCnpj(c.cnpj))
                                iniciarConsulta(c.cnpj, c.razao_social, 'escolha')
                            }}
                        />
                    </>
                )}

                {fase === 'relatorio' && (
                    <>
                        {carregando && !radar && <CarregandoComDicas status={detalhe?.status} />}
                        {detalhe?.status === 'ERRO' && (
                            <div style={{...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c'}}>
                                A consulta falhou: {detalhe.error_message || 'erro desconhecido'}
                            </div>
                        )}
                        {radar && (
                            <Ficha
                                radar={radar}
                                consultaId={detalhe?.id || null}
                                onExportarPdf={exportarPdf}
                                exportando={exportando}
                                criadoEm={detalhe?.finished_at || detalhe?.created_at || null}
                                onAtualizar={() => iniciarConsulta(radar.cnpj)}
                                onFeedback={() => setFeedbackModal(true)}
                                onSolicitarCertidoes={solicitarCertidoes}
                                emitindoTipos={emitindoTipos}
                            />
                        )}
                    </>
                )}
                {fase === 'relatorio' && radar && (
                    <AiDisclaimer text="Este relatório é gerado por inteligência artificial a partir de dados públicos e pode conter informações desatualizadas ou incompletas. Confirme os dados antes de utilizá-los em decisões." />
                )}
            </div>

            <Modal
                open={feedbackModal}
                onClose={() => setFeedbackModal(false)}
                aria-labelledby="feedback-modal-title"
                aria-describedby="feedback-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 480,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography id="feedback-modal-title" variant="h6" sx={{fontWeight: 600}}>
                            Enviar feedback
                        </Typography>
                        <IconButton onClick={() => setFeedbackModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography id="feedback-modal-description" sx={{mb: 2, color: '#64748b'}}>
                        {radar?.razao_social || detalhe?.cnpj || 'Fornecedor'}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Descreva sua análise sobre esta consulta..."
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        sx={{mb: 3}}
                    />
                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
                        <Button
                            variant="outlined"
                            onClick={() => setFeedbackModal(false)}
                            sx={{textTransform: 'none'}}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmitFeedback}
                            disabled={!feedbackText.trim() || feedbackSubmitting}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: VERDE_ESCURO,
                                '&:hover': {backgroundColor: VERDE_ESCURO},
                            }}
                        >
                            {feedbackSubmitting ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Snackbar
                open={feedbackToast.open}
                autoHideDuration={4000}
                onClose={() => setFeedbackToast((prev) => ({...prev, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    severity={feedbackToast.severity}
                    onClose={() => setFeedbackToast((prev) => ({...prev, open: false}))}
                >
                    {feedbackToast.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

/* ----------------------------- Barra de busca ----------------------------- */

// Sem seletor de período: decisão de produto (23/07/2026) — o Radar pesquisa SEMPRE os
// últimos 5 anos; o rótulo fixo aparece no cabeçalho da seção de contratos.
function BarraBusca(props: {
    inicio: boolean
    termo: string
    setTermo: (v: string) => void
    onConsultar: () => void
}) {
    // 11 dígitos (CPF) ou 14 (CNPJ) → o botão vira "Consultar" (senão é busca por nome).
    const qtdDigitos = props.termo.replace(/\D/g, '').length
    const ehDocumento = qtdDigitos === 11 || qtdDigitos === 14
    return (
        <div
            style={{
                background: `linear-gradient(135deg, ${VERDE_ESCURO} 0%, ${VERDE_ESCURO} 45%, ${VERDE} 135%)`,
                padding: props.inicio ? '52px 24px 60px' : '22px 24px',
                color: '#fff',
                transition: 'padding 0.3s',
            }}
        >
            <div style={{maxWidth: 960, margin: '0 auto'}}>
                {props.inicio && (
                    <div style={{textAlign: 'center', marginBottom: 26}}>
                        <div style={{fontSize: 44, marginBottom: 6}}>🛰️</div>
                        <h1 style={{margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: -0.5}}>
                            Radar de Fornecedor
                        </h1>
                        <p style={{margin: '8px auto 0', maxWidth: 560, opacity: 0.92, fontSize: 16}}>
                            Antes de homologar ou contratar, conheça o fornecedor: identidade,
                            sócios, histórico no PNCP e sanções — com segurança jurídica.
                        </p>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        background: '#fff',
                        padding: 10,
                        borderRadius: 14,
                        boxShadow: '0 10px 30px rgba(2,44,38,0.22)',
                        maxWidth: props.inicio ? 720 : '100%',
                        margin: '0 auto',
                    }}
                >
                    <TextField
                        value={props.termo}
                        onChange={(e) => {
                            const v = e.target.value
                            const digitos = v.replace(/\D/g, '')
                            // Máscara progressiva do campo único: com 11 dígitos é CPF;
                            // do 12º em diante REorganiza para CNPJ (formatCpfCnpj). Só
                            // mascara conteúdo exclusivamente numérico/pontuação e a
                            // partir do 11º dígito — nome de empresa segue intocado.
                            props.setTermo(
                                digitos.length >= 11 && /^[\d./-]*$/.test(v) ? formatCpfCnpj(v) : v
                            )
                        }}
                        placeholder="CNPJ (00.000.000/0000-00), CPF ou nome da empresa"
                        size="small"
                        style={{flex: 1, minWidth: 240}}
                        onKeyDown={(e) => e.key === 'Enter' && props.onConsultar()}
                    />
                    <Button
                        variant="contained"
                        onClick={props.onConsultar}
                        style={{
                            background: VERDE_ESCURO,
                            height: 40,
                            textTransform: 'none',
                            fontWeight: 700,
                            paddingInline: 22,
                        }}
                    >
                        {ehDocumento ? 'Consultar' : 'Buscar'}
                    </Button>
                </div>
                <p
                    style={{
                        maxWidth: props.inicio ? 720 : '100%',
                        margin: '10px auto 0',
                        padding: '10px 14px',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 6px 18px rgba(2,44,38,0.12)',
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        color: '#475569',
                        textAlign: 'center',
                    }}
                >
                    💡 A busca <strong style={{color: VERDE_ESCURO}}>mais certeira é pelo CNPJ</strong> —
                    traz todos os contratos da empresa. Pela razão social, localizamos a empresa pelos
                    contratos públicos, então pode não encontrar todas.
                </p>
            </div>
        </div>
    )
}

/* --------------------- Carregando (com dicas rotativas) ------------------- */

function CarregandoComDicas({status, mensagem}: {status?: string; mensagem?: string}) {
    const [i, setI] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setI((p) => (p + 1) % TIPS.length), 4500)
        return () => clearInterval(id)
    }, [])
    const t = TIPS[i % TIPS.length]
    return (
        <div style={card}>
            <div style={{display: 'flex', gap: 14, alignItems: 'center'}}>
                <CircularProgress size={22} sx={{color: VERDE_ESCURO}} />
                <span style={{color: '#475569'}}>
                    {mensagem || 'Coletando contratos no PNCP, identidade na Receita e sanções…'}
                    {status ? ` (${status})` : ''}
                </span>
            </div>
            <div style={{marginTop: 16, padding: '12px 14px', borderLeft: `4px solid ${t.cor}`, background: '#f8fafc', borderRadius: 8}}>
                <div style={{fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, marginBottom: 4}}>
                    VOCÊ SABIA?
                </div>
                <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 4}}>
                    {t.emoji} {t.titulo}
                </div>
                <div style={{color: '#475569', fontSize: 14, lineHeight: 1.5}}>{t.texto}</div>
            </div>
        </div>
    )
}

/* -------------------------------- Landing -------------------------------- */

function Landing({recentes, onAbrir}: {recentes: ConsultaOut[]; onAbrir: (id: string) => void}) {
    const passos = [
        {n: '1', t: 'Digite o CNPJ, o CPF ou o nome', d: 'Pelo nome, você escolhe a empresa certa.'},
        {n: '2', t: 'O Radar cruza as fontes', d: 'PNCP, Receita Federal e sanções, em segundos.'},
        {n: '3', t: 'Receba o Relatório de Inteligência', d: 'Identidade, sócios, histórico e 🟢🟡🔴 — auditável.'},
    ]
    return (
        <div style={{paddingTop: 28}}>
            <div
                style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: 22,
                    display: 'flex',
                    gap: 22,
                    flexWrap: 'wrap',
                    justifyContent: 'space-around',
                    textAlign: 'center',
                    marginBottom: 26,
                }}
            >
                {passos.map((p) => (
                    <div key={p.n} style={{maxWidth: 240}}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: VERDE_ESCURO,
                                color: '#fff',
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                            }}
                        >
                            {p.n}
                        </div>
                        <div style={{fontWeight: 700, color: VERDE_ESCURO}}>{p.t}</div>
                        <div style={{color: '#64748b', fontSize: 13}}>{p.d}</div>
                    </div>
                ))}
            </div>

            {recentes.length > 0 && (
                <div style={{...card, marginBottom: 26}}>
                    <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Suas consultas recentes</h3>
                    {recentes.map((c) => {
                        const v = VEREDITO_UI[(c.veredito as Veredito) || 'INDEFINIDO'] ?? VEREDITO_UI.INDEFINIDO
                        return (
                            <button
                                key={c.id}
                                onClick={() => onAbrir(c.id)}
                                style={{...botaoLinha, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 14px', marginTop: 8}}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = VERDE)}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
                            >
                                <div style={{textAlign: 'left', overflow: 'hidden'}}>
                                    <div style={{fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                        {c.razao_social || c.nome_informado || 'Fornecedor'}
                                    </div>
                                    {/* ConsultaOut não traz tipo_documento: 11 dígitos = CPF
                                        (inferência pelo tamanho, mesma regra do backend). */}
                                    <div style={{color: '#64748b', fontSize: 13}}>
                                        {c.cnpj.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ'}{' '}
                                        {formatCpfCnpj(c.cnpj)}
                                    </div>
                                </div>
                                <div style={{whiteSpace: 'nowrap', color: v.cor, fontWeight: 700, fontSize: 13}}>
                                    {c.status === 'CONCLUIDO'
                                        ? `${v.emoji} ${v.label}`
                                        : c.status === 'ERRO'
                                        ? '⚠️ erro'
                                        : '⏳ processando'}
                                </div>
                            </button>
                        )
                    })}
                </div>
            )}

            <h2 style={{color: VERDE_ESCURO, textAlign: 'center', marginBottom: 4}}>Você sabia?</h2>
            <p style={{textAlign: 'center', color: '#64748b', marginTop: 0, marginBottom: 22}}>
                Compliance e integridade nas contratações públicas — com base na Lei 14.133/2021 e
                na jurisprudência do TCU.
            </p>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 16,
                }}
            >
                {TIPS.map((t, i) => (
                    <div
                        key={i}
                        style={{
                            background: '#fff',
                            borderRadius: 14,
                            padding: 20,
                            borderTop: `4px solid ${t.cor}`,
                            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                        }}
                    >
                        <div style={{fontSize: 28, marginBottom: 8}}>{t.emoji}</div>
                        <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 6}}>{t.titulo}</div>
                        <div style={{color: '#475569', fontSize: 14, lineHeight: 1.5}}>{t.texto}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

/* ------------------------------- Escolha PJ ------------------------------ */

// A busca inicial não reconta contratos por CNPJ: todos os candidatos representam
// a amostra. A ficha detalhada confirma o total ao selecionar a empresa.
const TOOLTIP_QTD_APROXIMADA =
    'Contagem aproximada, estimada pela amostra desta busca. O total exato de contratos aparece ao abrir a ficha da empresa.'

function Escolha(props: {
    termo: string
    buscando: boolean
    buscaFeita: boolean
    candidatos: FornecedorCandidato[]
    onEscolher: (c: FornecedorCandidato) => void
}) {
    if (props.buscando) {
        return (
            <div style={{paddingTop: 8}}>
                <CarregandoComDicas mensagem={`Buscando "${props.termo}" no PNCP…`} />
            </div>
        )
    }
    return (
        <div style={{paddingTop: 8}}>
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                    {props.buscaFeita ? 'Empresas encontradas' : 'Busca'} para "{props.termo}"
                </h3>
                {props.buscaFeita && props.candidatos.length === 0 && (
                    <div style={{color: '#64748b'}}>
                        Nenhuma empresa encontrada com esse nome no PNCP. Tente outro termo ou use o
                        CNPJ.
                    </div>
                )}
                {props.candidatos.map((c) => (
                    <button
                        key={c.cnpj}
                        onClick={() => props.onEscolher(c)}
                        style={{
                            ...botaoLinha,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 12,
                            padding: '14px 16px',
                            marginTop: 10,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = VERDE)}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
                    >
                        <div style={{textAlign: 'left'}}>
                            <div style={{fontWeight: 700, color: '#0f172a'}}>{c.razao_social}</div>
                            <div style={{color: '#64748b', fontSize: 13}}>CNPJ {formatCnpj(c.cnpj)}</div>
                        </div>
                        <div
                            style={{textAlign: 'right', whiteSpace: 'nowrap'}}
                            title={TOOLTIP_QTD_APROXIMADA}
                        >
                            <div style={{fontWeight: 700, color: VERDE_ESCURO}}>
                                {`≈ ${c.qtd_contratos}`}
                            </div>
                            <div style={{color: '#94a3b8', fontSize: 12}}>
                                contratos (aprox.)
                            </div>
                        </div>
                    </button>
                ))}
                {props.candidatos.length > 0 && (
                    <div style={{marginTop: 12, color: '#64748b', fontSize: 12.5, lineHeight: 1.5}}>
                        <strong style={{color: VERDE_ESCURO}}>≈</strong> indica contagem aproximada,
                        estimada pela amostra desta busca. O total exato de contratos aparece ao abrir
                        a ficha da empresa.
                    </div>
                )}
            </div>
        </div>
    )
}

/* --------------------------------- Ficha --------------------------------- */

function motivoVeredito(r: RadarFornecedor): string {
    if (r.veredito === 'IMPEDIDO')
        return 'Sanção que impede licitar/contratar com a Administração (inidoneidade — CEIS/TCU).'
    if (r.veredito === 'ATENCAO') {
        const m: string[] = []
        if (r.sancoes?.some((s) => s.fonte === 'LISTA_SUJA')) m.push('Lista Suja (trabalho escravo)')
        if (r.sancoes?.some((s) => !s.abrange_licitar && s.fonte !== 'LISTA_SUJA')) m.push('sanção de alcance restrito')
        // Situação cadastral é da ficha PJ da Receita — não existe para CPF (fail-closed).
        if (!fichaEhCpf(r) && r.situacao_cadastral && r.situacao_cadastral !== 'ATIVA') m.push(`situação ${r.situacao_cadastral} na Receita`)
        // Explica O QUE motivou o amarelo (ex.: certidão POSITIVA emitida) — o badge
        // re-renderiza junto com a ficha quando o push do lote de certidões chega.
        const altos = (r.sinais || []).filter((s) => s.nivel === 'ALTO')
        if (altos.length) m.push(altos.slice(0, 3).map((s) => s.titulo).join('; '))
        return 'Atenção: ' + (m.length ? m.join('; ') + '.' : 'há pontos a verificar.')
    }
    if (r.veredito === 'APTO') return 'Sem impedimentos nem indícios de alto risco encontrados.'
    return 'Dados insuficientes para concluir.'
}

function resumoLinha(r: RadarFornecedor): string {
    const p: string[] = []
    // A situação na Receita saiu daqui: virou chip de registro no cabeçalho (antes ela
    // aparecia DUAS vezes na mesma tela — no subtítulo e na abertura deste resumo).
    // Esta linha é só o resumo do HISTÓRICO + sanções/alertas.
    const h = r.historico
    if (h?.qtd_contratacoes) p.push(`${brl(h.total_adjudicado)} em ${h.qtd_contratacoes} contrato(s)`)
    const nSanc = r.sancoes?.length || 0
    p.push(
        r.sancoes?.some((s) => s.abrange_licitar)
            ? 'com sanção impeditiva'
            : nSanc
            ? `${nSanc} sanção(ões) não impeditiva(s)`
            : r.sancoes_verificadas
            ? 'sem sanções'
            : 'sanções não verificadas'
    )
    if (r.sinais?.length) p.push(`${r.sinais.length} alerta(s)`)
    return p.join(' · ')
}

function Ficha({
    radar,
    consultaId,
    onExportarPdf,
    exportando,
    criadoEm,
    onAtualizar,
    onFeedback,
    onSolicitarCertidoes,
    emitindoTipos,
}: {
    radar: RadarFornecedor
    consultaId: string | null
    onExportarPdf: () => void
    exportando: boolean
    criadoEm: string | null
    onAtualizar: () => void
    onFeedback: () => void
    onSolicitarCertidoes: (tipos: string[], dadosPf?: DadosPfCertidoes) => Promise<void>
    emitindoTipos: string[] | null
}) {
    const v = VEREDITO_UI[radar.veredito] ?? VEREDITO_UI.INDEFINIDO
    const h = radar.historico
    // Ficha de pessoa física (CPF): esconde tudo que é exclusivo de PJ — identidade da
    // Receita (CNAE, porte, capital, QSA…), nome fantasia e situação cadastral. Fichas
    // antigas, sem tipo_documento, seguem como CNPJ (compat).
    const ehCpf = fichaEhCpf(radar)
    // Cadastro no SICAF (Compras.gov.br, grátis) — ver `resumoSicaf`: vale p/ CNPJ e CPF.
    const sicaf = resumoSicaf(radar)

    return (
        <div style={{paddingTop: 8}}>
            {/* Cabeçalho + veredito + ações */}
            <div style={card}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12}}>
                    <div>
                        <div style={{fontSize: 19, fontWeight: 800, color: '#0f172a'}}>
                            {/* PF sem nome (razao_social null): o título é o próprio CPF mascarado. */}
                            {radar.razao_social || (ehCpf ? formatCpf(radar.cnpj) : 'Fornecedor')}
                            {!ehCpf && radar.nome_fantasia ? (
                                <span style={{fontWeight: 500, color: '#64748b'}}> · {radar.nome_fantasia}</span>
                            ) : null}
                        </div>
                        {/* Linha 2 = IDENTIFICAÇÃO (quem e onde), só isso. Os estados em
                            registros públicos saem daqui e viram chips na linha 3: antes,
                            documento, cidade, situação na Receita e situação no SICAF
                            disputavam a mesma fileira de pontos, com o mesmo peso. */}
                        <div style={{color: '#64748b', fontSize: 14, marginTop: 2}}>
                            {ehCpf ? 'CPF' : 'CNPJ'} {formatCpfCnpj(radar.cnpj)}
                            {radar.municipio ? ` · ${radar.municipio}/${radar.uf || ''}` : radar.uf ? ` · ${radar.uf}` : ''}
                        </div>
                        {/* Linha 3 = REGISTROS PÚBLICOS: um chip por fonte, cada um com o
                            seu estado e a sua ação. Some inteira quando não há o que dizer. */}
                        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8}}>
                            {/* Situação cadastral (Receita) é exclusiva de PJ — some na ficha CPF. */}
                            {!ehCpf && radar.situacao_cadastral && (
                                <ChipRegistro
                                    fonte="Receita"
                                    valor={radar.situacao_cadastral}
                                    tom={radar.situacao_cadastral === 'ATIVA' ? 'ok' : 'alerta'}
                                    ajuda="Situação cadastral do CNPJ na Receita Federal (via BrasilAPI)."
                                />
                            )}
                            {/* SICAF (Compras.gov.br, grátis) — vale p/ CNPJ e CPF. Só
                                aparece quando a fonte RESPONDEU: `sicaf_verificado=false`
                                não distingue "a fonte caiu agora" de "ficha anterior a
                                esta fonte", então o cabeçalho não afirma nada (o PDF da
                                ficha registra o "não verificado nesta consulta"). */}
                            {radar.sicaf_verificado && radar.sicaf && (
                                <ChipRegistro
                                    fonte="SICAF"
                                    valor={sicaf.texto}
                                    tom={sicaf.tom}
                                    ajuda={sicaf.ajuda}
                                    acao={{
                                        href: LINK_SICAF_CRC,
                                        copiar: radar.cnpj,
                                        ajuda: 'Consulta pública do Certificado de Registro Cadastral (CRC) no SICAF: mostra os níveis de cadastro e as validades. Não exige login. O documento é copiado ao clicar — cole no campo da página e resolva o captcha (a página do governo não aceita o documento por link).',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                        <div
                            title={motivoVeredito(radar)}
                            style={{
                                background: v.fundo,
                                color: v.cor,
                                fontWeight: 800,
                                padding: '10px 18px',
                                borderRadius: 999,
                                whiteSpace: 'nowrap',
                                fontSize: 15,
                                cursor: 'help',
                            }}
                        >
                            {v.emoji} {v.label}
                        </div>
                        <Button onClick={onAtualizar} variant="text" size="small" style={{textTransform: 'none', color: VERDE_ESCURO, minWidth: 0}}>
                            ↻ Atualizar
                        </Button>
                        <Button
                            onClick={onExportarPdf}
                            disabled={exportando}
                            variant="outlined"
                            size="small"
                            style={{textTransform: 'none', color: VERDE_ESCURO, borderColor: VERDE}}
                        >
                            {exportando ? 'Gerando…' : '⬇ Exportar PDF'}
                        </Button>
                        <Button
                            onClick={onFeedback}
                            variant="outlined"
                            size="small"
                            style={{textTransform: 'none', color: VERDE_ESCURO, borderColor: VERDE}}
                        >
                            💬 Enviar Feedback
                        </Button>
                    </div>
                </div>
                {/* Resumo executivo + carimbo de data */}
                <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                    <div style={{fontSize: 14, color: '#334155'}}>{resumoLinha(radar)}</div>
                    {criadoEm && (
                        <div style={{fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap'}}>
                            Consultado em {fmtDataHora(criadoEm)}
                        </div>
                    )}
                </div>
            </div>

            {/* Alertas honestos */}
            {radar.alertas?.length > 0 && (
                <div style={{...card, borderColor: '#fde68a', background: '#fffbeb'}}>
                    {radar.alertas.map((a, i) => (
                        <div key={i} style={{color: '#92400e', fontSize: 14, marginBottom: 4}}>
                            ⚠️ {a}
                        </div>
                    ))}
                </div>
            )}

            {/* PF não tem ficha cadastral de PJ na Receita: CNAE, porte, capital social,
                natureza jurídica, QSA e o link do cnpjreva não se aplicam (o backend manda
                tudo null para CPF) — o cartão inteiro sai da ficha. */}
            {!ehCpf && <IdentidadeCard radar={radar} />}

            {/* SÓ ficha PF: vínculo do titular como servidor do Executivo federal —
                potencial vedação (arts. 9º, §1º, e 14 da Lei 14.133). Ficha CNPJ nunca
                mostra o bloco (o backend nem manda os campos). */}
            {ehCpf && <SecaoVinculoServidor radar={radar} />}

            {/* Métricas */}
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                    Histórico de contratações{' '}
                    <span style={countStyle}>· últimos 5 anos · PNCP + Portal da Transparência</span>
                </h3>
                <div style={{display: 'flex', gap: 28, flexWrap: 'wrap'}}>
                    <Metric label="Total adjudicado" value={brl(h.total_adjudicado)} />
                    <Metric label="Contratos" value={String(h.qtd_contratacoes)} />
                    <Metric label="Órgãos" value={String(h.qtd_orgaos)} />
                    <Metric
                        label="Concentração no maior órgão"
                        value={h.concentracao_top_orgao != null ? `${Math.round(h.concentracao_top_orgao * 100)}%` : '—'}
                    />
                </div>
                {h.top_objeto && (
                    <div style={{marginTop: 12, color: '#475569', fontSize: 14}}>
                        Objeto predominante: <b>{h.top_objeto}</b>
                        {h.ufs?.length ? ` · UFs: ${h.ufs.join(', ')}` : ''}
                    </div>
                )}
            </div>

            <SecaoContratos radar={radar} />

            {/* Alertas / indícios */}
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Alertas e indícios</h3>
                {radar.sinais?.length ? (
                    radar.sinais.map((s: SinalIrregularidade, i) => (
                        <div
                            key={i}
                            style={{
                                borderLeft: `4px solid ${NIVEL_COR[s.nivel] || '#475569'}`,
                                padding: '8px 12px',
                                marginBottom: 10,
                                background: '#f8fafc',
                                borderRadius: 6,
                            }}
                        >
                            <div style={{fontWeight: 600, color: NIVEL_COR[s.nivel] || '#334155'}}>
                                {s.titulo} <span style={{fontSize: 11, fontWeight: 700}}>[{s.nivel}]</span>
                            </div>
                            <div style={{color: '#475569', fontSize: 13}}>{s.detalhe}</div>
                        </div>
                    ))
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>Nenhum alerta no histórico.</div>
                )}
            </div>

            <Sancoes radar={radar} />

            <SecaoCertidoes
                radar={radar}
                consultaId={consultaId}
                onSolicitar={onSolicitarCertidoes}
                emitindoTipos={emitindoTipos}
            />

            <SecaoDiarios radar={radar} />

            <SecaoMidia radar={radar} />

            <SecaoIbama radar={radar} />

            <SecaoProtestos radar={radar} />

            {/* Disclaimer (as fontes já estão distribuídas ao longo do relatório) */}
            <div style={{...card, background: '#f8fafc'}}>
                <div style={{fontSize: 12, color: '#64748b', lineHeight: 1.6}}>
                    Informações indicativas, coletadas de fontes públicas na data da consulta. Não
                    substituem a verificação oficial nem constituem juízo sobre idoneidade.
                </div>
            </div>
        </div>
    )
}

type TomRegistro = 'ok' | 'alerta' | 'neutro'

// Chip de REGISTRO PÚBLICO (Receita, SICAF…): rótulo da fonte + estado dela. Existe porque
// o cabeçalho vinha empilhando fatos de naturezas diferentes (documento, cidade, situação na
// Receita, situação no SICAF, link) numa única fileira separada por pontos, todos com o
// mesmo peso visual — o gestor tinha que ler a frase inteira para achar o que importava.
const TONS_REGISTRO: Record<TomRegistro, {texto: string; fundo: string; borda: string}> = {
    ok: {texto: '#15803d', fundo: '#f0fdf4', borda: '#bbf7d0'},
    alerta: {texto: '#b45309', fundo: '#fffbeb', borda: '#fde68a'},
    neutro: {texto: '#475569', fundo: '#f8fafc', borda: '#e2e8f0'},
}

function ChipRegistro({
    fonte,
    valor,
    tom,
    ajuda,
    acao,
}: {
    fonte: string
    valor: string
    tom: TomRegistro
    ajuda?: string
    acao?: {href: string; ajuda?: string; copiar?: string}
}) {
    const c = TONS_REGISTRO[tom]
    // A página do SICAF não aceita documento por link (formulário JSF com ViewState de
    // sessão, sem view param): o mais perto de "já vir digitado" é copiar no clique, p/ a
    // pessoa só colar. Best-effort — sem área de transferência, o link funciona igual.
    const [copiado, setCopiado] = useState(false)
    const copiar = () => {
        if (!acao?.copiar) return
        navigator.clipboard
            ?.writeText(acao.copiar)
            .then(() => {
                setCopiado(true)
                setTimeout(() => setCopiado(false), 2500)
            })
            .catch(() => undefined)
    }
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: c.fundo,
                border: `1px solid ${c.borda}`,
                fontSize: 13,
                whiteSpace: 'nowrap',
            }}
        >
            <span style={{color: '#94a3b8', fontWeight: 600}}>{fonte}</span>
            {/* Com ação, o PRÓPRIO estado é o link (mais a seta): o rótulo extra ao lado
                virava um terceiro texto disputando a mesma linha. */}
            {acao ? (
                <a
                    href={acao.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={[ajuda, acao.ajuda].filter(Boolean).join(' ')}
                    onClick={copiar}
                    style={{color: c.texto, fontWeight: 700, textDecoration: 'none'}}
                >
                    {valor} ↗
                </a>
            ) : (
                <b title={ajuda} style={{color: c.texto, cursor: ajuda ? 'help' : undefined}}>
                    {valor}
                </b>
            )}
            {copiado && (
                <span style={{fontSize: 11, color: '#15803d'}}>documento copiado — é só colar</span>
            )}
        </span>
    )
}

export function resumoSicaf(radar: RadarFornecedor): {
    texto: string
    cor: string
    tom: TomRegistro
    ajuda: string
} {
    const sic = radar.sicaf
    if (!radar.sicaf_verificado || !sic)
        return {
            texto: 'não verificado',
            cor: '#94a3b8',
            tom: 'neutro',
            ajuda: 'A consulta ao cadastro do SICAF (Compras.gov.br) não respondeu nesta consulta.',
        }
    if (!sic.cadastrado)
        return {
            texto: 'não cadastrado',
            cor: '#64748b',
            tom: 'neutro',
            ajuda:
                'Documento não localizado no SICAF. O SICAF é o registro de fornecedores do ' +
                'Executivo Federal (SISG): a ausência NÃO indica irregularidade — é comum em ' +
                'fornecedor que atende apenas órgãos estaduais e municipais.',
        }
    const motivos = [
        sic.ativo === false ? 'cadastro inativo' : null,
        sic.habilitado_licitar === false ? 'não habilitado a licitar' : null,
    ].filter(Boolean) as string[]
    if (motivos.length)
        return {
            texto: motivos.join(' · '),
            cor: '#b45309',
            tom: 'alerta',
            ajuda:
                `SICAF: ${motivos.join(' e ')}. O cadastro federal costuma refletir documento ` +
                'vencido NO SICAF — veja quais níveis venceram em "ver validades" (consulta ' +
                'pública do CRC) e confronte com as certidões desta ficha, emitidas direto na ' +
                'fonte. Não é, por si só, impedimento de licitar.',
        }
    // Sinalizador NÃO informado pela fonte (shape novo/null): afirma só o que se sabe —
    // "cadastro ativo" —, nunca "habilitado a licitar" (que a fonte não disse).
    if (sic.habilitado_licitar !== true)
        return {
            texto: 'cadastro ativo',
            cor: '#64748b',
            tom: 'neutro',
            ajuda:
                'Cadastro ativo no SICAF (dados abertos do Compras.gov.br). A fonte não ' +
                'informou o sinalizador de habilitação para licitar nesta consulta — ' +
                'confirme na Declaração de Situação do Fornecedor (acesso do órgão ao SICAF).',
        }
    return {
        texto: 'habilitado a licitar',
        cor: '#15803d',
        tom: 'ok',
        ajuda:
            'Cadastro ativo e habilitado a licitar no SICAF (dados abertos do Compras.gov.br). ' +
            'A API não devolve a VALIDADE de cada nível: use "ver validades" (consulta pública ' +
            'do CRC). As certidões desta ficha são emitidas direto na fonte.',
    }
}

function IdentidadeCard({radar}: {radar: RadarFornecedor}) {
    const linhas: [string, ReactNode][] = []
    if (radar.cnae) linhas.push(['Atividade (CNAE)', radar.cnae])
    if (radar.cnaes_secundarios?.length)
        linhas.push(['Atividades secundárias', radar.cnaes_secundarios.join(' · ')])
    if (radar.natureza_juridica) linhas.push(['Natureza jurídica', radar.natureza_juridica])
    if (radar.porte) linhas.push(['Porte', radar.porte])
    if (typeof radar.capital_social === 'number') linhas.push(['Capital social', brl(radar.capital_social)])
    if (radar.data_abertura) linhas.push(['Abertura', fmtData(radar.data_abertura)])
    if (radar.municipio || radar.uf)
        linhas.push(['Município/UF', `${radar.municipio || ''}${radar.uf ? '/' + radar.uf : ''}`])
    if (radar.endereco)
        linhas.push([
            'Endereço (sede)',
            <>
                {radar.endereco}{' '}
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([radar.endereco, radar.municipio, radar.uf].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                >
                    mapa ↗
                </a>
            </>,
        ])
    if (radar.cep) linhas.push(['CEP', radar.cep])
    if (radar.telefone)
        linhas.push([
            'Telefone',
            <a href={`tel:${radar.telefone.replace(/\D/g, '')}`} style={linkStyle}>
                {radar.telefone}
            </a>,
        ])
    if (radar.email)
        linhas.push([
            'E-mail',
            <a href={`mailto:${radar.email}`} style={linkStyle}>
                {radar.email}
            </a>,
        ])
    if (radar.optante_simples != null)
        linhas.push(['Simples Nacional', radar.optante_simples ? 'Optante' : 'Não optante'])

    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>Identidade (Receita Federal)</h3>
                <div style={{fontSize: 13}}>
                    <a href={LINK_RECEITA} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Consultar na Receita ↗
                    </a>
                </div>
            </div>
            {linhas.length > 0 && (
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 18px', marginTop: 12}}>
                    {linhas.map(([k, val], i) => (
                        <div key={i} style={{fontSize: 14, color: '#475569'}}>
                            <span style={{color: '#94a3b8'}}>{k}: </span>
                            <b style={{color: '#0f172a'}}>{val}</b>
                        </div>
                    ))}
                </div>
            )}
            {radar.socios?.length > 0 && <SociosLista socios={radar.socios} />}
        </div>
    )
}

function SociosLista({socios}: {socios: Socio[]}) {
    const [pagina, setPagina] = useState(1)
    const POR_PAG = 8
    const totalPaginas = Math.max(1, Math.ceil(socios.length / POR_PAG))
    const ini = (pagina - 1) * POR_PAG
    const itens = socios.slice(ini, ini + POR_PAG)
    return (
        <div style={{marginTop: 16}}>
            <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 14}}>
                Sócios e administradores <span style={countStyle}>({socios.length})</span>
            </div>
            {itens.map((s: Socio, i) => (
                <div
                    key={ini + i}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '6px 0',
                        borderTop: i ? '1px solid #f1f5f9' : 'none',
                        fontSize: 13,
                    }}
                >
                    <span style={{color: '#0f172a', fontWeight: 600}}>{s.nome || '—'}</span>
                    <span style={{color: '#64748b'}}>
                        {s.qualificacao || ''}
                        {s.data_entrada ? ` · desde ${fmtData(s.data_entrada)}` : ''}
                    </span>
                </div>
            ))}
            {totalPaginas > 1 && (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 12}}>
                    <Button size="small" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        ← Anterior
                    </Button>
                    <span style={{color: '#475569', fontSize: 13}}>
                        Página {pagina} de {totalPaginas}
                    </span>
                    <Button size="small" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        Próxima →
                    </Button>
                </div>
            )}
        </div>
    )
}

function Sancoes({radar}: {radar: RadarFornecedor}) {
    // Data de referência no fuso de Brasília (não em UTC): à noite o dia UTC já virou e uma
    // sanção que termina HOJE apareceria como expirada.
    const hoje = hojeBR()
    const vigente = (s: Sancao) => !s.data_fim || s.data_fim >= hoje
    // ativas (vigentes) primeiro; dentro disso, as impeditivas no topo
    const sancoes = [...(radar.sancoes || [])].sort(
        (a, b) => Number(vigente(b)) - Number(vigente(a)) || Number(!!b.abrange_licitar) - Number(!!a.abrange_licitar)
    )
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Sanções e impedimentos{' '}
                    {!radar.sancoes_verificadas && (
                        <span style={{fontSize: 12, color: '#b45309', fontWeight: 400}}>— não verificadas</span>
                    )}
                </h3>
                <div style={{fontSize: 13}}>
                    <a href={linkTransparencia(radar.cnpj)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Portal da Transparência ↗
                    </a>{' '}·{' '}
                    <a href={LINK_TCU} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Inidôneos do TCU ↗
                    </a>
                </div>
            </div>
            <div style={{marginTop: 10}}>
                {sancoes.length ? (
                    sancoes.map((s, i) => {
                        const impede = !!s.abrange_licitar
                        const viva = vigente(s)
                        return (
                            <div key={i} style={{fontSize: 14, color: viva ? (impede ? '#b91c1c' : '#b45309') : '#94a3b8', marginBottom: 6}}>
                                {viva ? (impede ? '🔴' : '🟡') : '⚪'} {FONTE_SANCAO_LABEL[s.fonte] || s.fonte} — {s.descricao || 'sanção registrada'}
                                {s.data_fim
                                    ? viva
                                        ? ` · vigente até ${fmtData(s.data_fim)}`
                                        : ` · expirada em ${fmtData(s.data_fim)}`
                                    : ''}
                                {viva && !impede && <span style={{color: '#94a3b8'}}> · alcance restrito</span>}
                                {s.link && (
                                    <>
                                        {' '}
                                        <a href={s.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            evidência ↗
                                        </a>
                                    </>
                                )}
                            </div>
                        )
                    })
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>
                        {radar.sancoes_verificadas
                            ? 'Nenhuma sanção ativa encontrada.'
                            : 'Confirme manualmente no Portal da Transparência até a checagem automática entrar no ar.'}
                    </div>
                )}
            </div>
        </div>
    )
}

function SecaoIbama({radar}: {radar: RadarFornecedor}) {
    const aut = radar.autuacoes_ambientais || []
    if (!radar.ibama_verificado && aut.length === 0) return null
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Ambiental — IBAMA <span style={countStyle}>({aut.length})</span>
                </h3>
                <a
                    href="https://servicos.ibama.gov.br/ctf/publico/areasembargadas/ConsultaPublicaAreasEmbargadas.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                >
                    Consulta IBAMA ↗
                </a>
            </div>
            <div style={{marginTop: 10}}>
                {aut.length ? (
                    <>
                        {aut.slice(0, 20).map((a: AutuacaoAmbiental, i) => (
                            <div key={i} style={{fontSize: 14, color: a.ativo ? '#b45309' : '#64748b', marginBottom: 6}}>
                                {a.ativo ? '🟠' : '⚪'} <b>{a.tipo}</b>
                                {a.descricao ? ` — ${a.descricao}` : ''}
                                {a.municipio ? ` · ${a.municipio}/${a.uf || ''}` : ''}
                                {a.data ? ` · ${fmtData(a.data)}` : ''}
                                {typeof a.valor_multa === 'number' ? ` · multa ${brl(a.valor_multa)}` : ''}
                                {!a.ativo && <span style={{color: '#94a3b8'}}> · regularizado/desembargado</span>}
                                {a.link && (
                                    <>
                                        {' '}
                                        <a href={a.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            ver ↗
                                        </a>
                                    </>
                                )}
                            </div>
                        ))}
                        <div style={fonteNota}>
                            Indício ambiental (autos/embargos) — não impede licitar por si só. Confira a situação de cada registro.
                        </div>
                    </>
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>Nenhum auto/embargo ambiental localizado para este CNPJ.</div>
                )}
            </div>
        </div>
    )
}

function SecaoProtestos({radar}: {radar: RadarFornecedor}) {
    const protestos = radar.protestos || []
    // Mesmo gate das certidões sob demanda: a seção só aparece depois que a fonte
    // (IEPTB, paga) foi consultada — ou se houver registros persistidos.
    if (!radar.protestos_verificados && protestos.length === 0) return null
    return (
        <div style={card}>
            <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                Protestos em cartório <span style={countStyle}>(informativo · {protestos.length})</span>
            </h3>
            {protestos.length ? (
                <>
                    <div style={avisoHomonimo}>
                        ⚠️ Informativo — protesto <b>não impede licitar</b> nem afeta o veredito.
                        Indicador de saúde financeira para a diligência.
                    </div>
                    {protestos.slice(0, 20).map((p: Protesto, i) => (
                        <div key={i} style={{fontSize: 14, color: '#b45309', marginBottom: 6}}>
                            🟠 <b style={{color: '#0f172a'}}>{p.cartorio || 'Cartório não informado'}</b>
                            {p.comarca ? ` · ${p.comarca}${p.uf ? '/' + p.uf : ''}` : p.uf ? ` · ${p.uf}` : ''}
                            {p.data ? ` · ${fmtData(p.data)}` : ''}
                            {typeof p.valor === 'number' ? ` · ${brl(p.valor)}` : ''}
                            {p.detalhe ? <span style={{color: '#64748b'}}> · {p.detalhe}</span> : ''}
                        </div>
                    ))}
                </>
            ) : (
                <div style={{color: '#64748b', fontSize: 14}}>
                    Nenhum protesto localizado para este CNPJ (IEPTB).
                </div>
            )}
            <div style={fonteNota}>
                Fonte: cartórios de protesto (IEPTB), consultada sob demanda junto com as
                certidões.
            </div>
        </div>
    )
}

function SecaoDiarios({radar}: {radar: RadarFornecedor}) {
    const ms = radar.noticias_oficiais || []
    const termo = radar.nome_busca || radar.razao_social || ''
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Menções em Diários Oficiais <span style={countStyle}>({ms.length})</span>
                </h3>
                {termo && (
                    <div style={{fontSize: 13}}>
                        <a href={buscaQueridoDiario(termo)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                            Diários municipais/estaduais ↗
                        </a>{' '}·{' '}
                        <a href={buscaDOU(termo)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                            DOU ↗
                        </a>
                    </div>
                )}
            </div>
            <div style={{marginTop: 10}}>
                {ms.length ? (
                    <>
                        <div style={avisoHomonimo}>
                            ⚠️ Resultados por nome — podem incluir homônimos. Confira o diário-fonte.
                        </div>
                        {ms.slice(0, 14).map((m: MencaoOficial, i) => (
                            <div key={i} style={{borderLeft: `3px solid ${VERDE}`, padding: '6px 12px', marginBottom: 8, background: '#f8fafc', borderRadius: 6}}>
                                <div style={{fontSize: 13, color: '#0f172a', fontWeight: 600}}>
                                    {m.data ? fmtData(m.data) : ''}
                                    {m.origem
                                        ? ` · ${m.origem}`
                                        : m.municipio
                                        ? ` · ${m.municipio}/${m.uf || ''}`
                                        : ''}
                                    {m.link && (
                                        <>
                                            {' '}
                                            <a href={m.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                                {m.origem ? 'matéria ↗' : 'diário ↗'}
                                            </a>
                                        </>
                                    )}
                                </div>
                                {m.trecho && <div style={{fontSize: 13, color: '#475569', marginTop: 2}}>{m.trecho}</div>}
                            </div>
                        ))}
                    </>
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>
                        Nenhuma menção localizada nos diários cobertos — confira manualmente nos links acima.
                    </div>
                )}
                <div style={fonteNota}>
                    Cobertura parcial (nem todo município/órgão está coberto) — a ausência de menção não prova nada.
                </div>
            </div>
        </div>
    )
}

function SecaoMidia({radar}: {radar: RadarFornecedor}) {
    const ms = radar.mencoes_midia || []
    if (!radar.midia_verificada && ms.length === 0) return null
    return (
        <div style={card}>
            <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                Reputação na mídia <span style={countStyle}>(informativo · {ms.length})</span>
            </h3>
            {ms.length ? (
                <>
                    <div style={avisoHomonimo}>
                        ⚠️ Informativo — <b>não afeta o veredito</b>. Resultados por nome, podem incluir homônimos.
                    </div>
                    {ms.slice(0, 12).map((m: MencaoMidia, i) => {
                        const s = SENT_UI[m.sentimento || 'NEUTRO'] || SENT_UI.NEUTRO
                        return (
                            <div key={i} style={{fontSize: 14, marginBottom: 6}}>
                                <span title={s.rotulo}>{s.emoji}</span> <b style={{color: '#0f172a'}}>{m.veiculo}</b>
                                {m.data ? <span style={{color: '#94a3b8'}}> · {fmtData(m.data)}</span> : ''} ·{' '}
                                {m.url ? (
                                    <a href={m.url} target="_blank" rel="noopener noreferrer" style={{color: s.cor, textDecoration: 'none'}}>
                                        {m.titulo || '(sem título)'} ↗
                                    </a>
                                ) : (
                                    m.titulo || ''
                                )}
                            </div>
                        )
                    })}
                    <div style={fonteNota}>
                        Tom (🔻 negativo / 🔺 positivo / • neutro) é orientativo, por palavra-chave no título.
                    </div>
                </>
            ) : (
                <div style={{color: '#64748b', fontSize: 14}}>Nenhuma matéria recente localizada para este nome.</div>
            )}
        </div>
    )
}

/* ----------------- Vínculo com a Administração federal (ficha PF) ----------------- */

// Vínculo do titular (CPF) como servidor do Executivo federal: servidor participando de
// licitação/contratação é potencial vedação (arts. 9º, §1º, e 14 da Lei 14.133). Três
// estados honestos: vínculo encontrado (alerta), checagem não rodou (ficha antiga ou
// servidor_verificado=false → "não verificado", fail-closed) e checagem ok sem vínculo
// (nota de que a cobertura é SÓ o Executivo federal — ausência não prova nada fora dele).
function SecaoVinculoServidor({radar}: {radar: RadarFornecedor}) {
    const vinculos: VinculoServidor[] = radar.vinculos_servidor || []
    const verificado = radar.servidor_verificado === true
    return (
        <div style={card}>
            <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                Vínculo com a Administração federal{' '}
                <span style={countStyle}>· Executivo federal</span>
            </h3>
            {vinculos.length > 0 ? (
                <>
                    <div
                        style={{
                            ...avisoHomonimo,
                            color: '#b91c1c',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                        }}
                    >
                        ⚠️ Vínculo de servidor identificado — <b>potencial vedação</b> à
                        participação/contratação (arts. 9º, §1º, e 14 da Lei 14.133). Confirme o
                        órgão de lotação e o regime do vínculo antes de prosseguir.
                    </div>
                    {vinculos.map((v, i) => (
                        <div key={i} style={{fontSize: 14, color: '#b91c1c', marginBottom: 6}}>
                            🔴 <b style={{color: '#0f172a'}}>{v.orgao || 'Órgão não informado'}</b>
                            {v.cargo ? ` · ${v.cargo}` : ''}
                            {v.situacao ? <span style={{color: '#64748b'}}> · {v.situacao}</span> : ''}
                        </div>
                    ))}
                </>
            ) : verificado ? (
                <div style={{color: '#64748b', fontSize: 14}}>
                    Nenhum vínculo ativo localizado. A checagem cobre somente o Executivo
                    federal — vínculos estaduais, municipais e dos demais Poderes não são
                    verificados automaticamente.
                </div>
            ) : (
                <div style={{color: '#b45309', fontSize: 14}}>
                    Vínculo com a Administração não verificado nesta consulta.
                </div>
            )}
        </div>
    )
}

// Explicação da trava do SINTEGRA: a CND Estadual só sai com a Inscrição Estadual em mãos,
// então as duas emissões andam juntas (e as duas são contadas no botão).
const MOTIVO_TRAVA_SINTEGRA = 'necessário para emitir a CND Estadual — sai junto no mesmo lote'

// Rótulos e larguras dos campos do mini-form de dados do titular (ficha PF). Quais campos
// aparecem/são exigidos é decisão PURA de camposPf (certidoesUi) — aqui é só apresentação.
const CAMPO_PF_UI: Record<keyof DadosPfCertidoes, {label: string; largura: number}> = {
    data_nascimento: {label: 'Data de nascimento', largura: 200},
    nome: {label: 'Nome completo', largura: 250},
    nome_mae: {label: 'Nome da mãe', largura: 250},
    nome_pai: {label: 'Nome do pai', largura: 250},
    uf_nascimento: {label: 'UF de nascimento', largura: 170},
    uf: {label: 'UF de domicílio', largura: 170},
}

// UFs para os selects do mini-form (uf de domicílio do MPT / uf de nascimento).
const UFS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
    'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

export function SecaoCertidoes({
    radar,
    consultaId,
    onSolicitar,
    emitindoTipos = null,
}: {
    radar: RadarFornecedor
    consultaId: string | null
    onSolicitar: (tipos: string[], dadosPf?: DadosPfCertidoes) => Promise<void>
    // Tipos do lote em emissão AGORA (sessão viva) → "Emitindo…" preciso por linha. null
    // (ficha reaberta) = marca toda linha ainda não emitida enquanto o status for CONSULTANDO.
    emitindoTipos?: string[] | null
}) {
    const certidoes = radar.certidoes || []
    // Ficha CPF: a emissão sob demanda foi DESTRAVADA para pessoa física (backend
    // 27/08/2026) com um conjunto próprio de tipos. Duas linhas seguem só-orientação
    // (TSE_QUITACAO / JUSTICA_ESTADUAL — `ehEmitivel` já as exclui, nunca ganham
    // checkbox) e algumas exigem dados do titular, coletados no mini-form abaixo.
    const ehCpf = fichaEhCpf(radar)
    // Estado do bloco sob demanda; consultas ANTIGAS (sem o campo, com certidões reais)
    // são tratadas como CONCLUIDAS (compat).
    const status: string =
        radar.certidoes_status ?? (certidoes.length ? 'CONCLUIDAS' : 'NAO_SOLICITADAS')
    const [erroSolicitar, setErroSolicitar] = useState<string | null>(null)
    const [solicitando, setSolicitando] = useState(false)
    // EMISSÃO SELETIVA (decisão do dono, 27/07): cada certidão é uma emissão PAGA, então o
    // usuário marca QUAIS emitir — tudo começa DESMARCADO e nada emite até a escolha.
    const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set())
    // Vigência p/ decidir pendência (mesma data dos chips): certidão vencida vira pendência.
    // A data de referência é o dia em BRASÍLIA — com o dia UTC, entre 21h e meia-noite BRT
    // uma certidão válida até hoje já aparecia "Vencida — reemitir" sem precisar.
    const hoje = hojeBR()
    // REEMISSÃO LIBERADA (15/10/2026): o recibo do provedor expira em 7 dias, então TODA
    // linha com rota automática pode (re)emitir — inclusive vigente/SEM_INSCRICAO. A guarda
    // de crédito (dono 30/07) continua: nada emite sem checkbox explícito, e o "Selecionar
    // todas" marca só pendências (ehPendente), nunca reemissão em massa de vigentes.
    const tiposElegiveis = certidoes.filter((c) => ehEmitivel(c, hoje, ehCpf)).map((c) => c.tipo)
    // "Checar todas" marca SÓ as pendências — reemissão de certidão vigente é escolha
    // individual (recibo do provedor expira em 7d, mas re-emitir tudo custa crédito).
    const tiposPendentes = certidoes.filter((c) => ehPendente(c, hoje, ehCpf)).map((c) => c.tipo)
    const temElegiveis = tiposElegiveis.length > 0
    // Controles (checkbox + "Emitir") só quando dá para emitir: ficha com AO MENOS uma linha
    // emitível e fora do estado "emitindo". Durante CONSULTANDO some (spinner); sem elegível
    // (tudo já emitido/vigente) também não aparece. Vale para CNPJ E para CPF.
    const mostrarControles = !!consultaId && status !== 'CONSULTANDO' && temElegiveis
    const todasMarcadas = tiposPendentes.length > 0 && todasSelecionadas(tiposPendentes, selecionadas)
    // Mini-form de dados do titular (SÓ ficha CPF): os campos aparecem conforme os
    // checkboxes marcados (camposPf) e as exigências vazias travam o "Emitir".
    const [dadosPf, setDadosPf] = useState<DadosPfCertidoes>({})
    const camposForm = ehCpf ? camposPf(selecionadas) : []
    const faltantes = ehCpf ? faltamDadosPf(selecionadas, dadosPf) : []
    // Marcar a CND ESTADUAL leva o SINTEGRA junto (o backend exige a Inscrição Estadual para
    // emitir a estadual e adiciona a linha ao lote por conta própria): ficam marcados os DOIS,
    // o contador do botão diz "(2)" e o SINTEGRA fica travado — a UI conta o que será cobrado.
    const marcarUma = (tipo: string) =>
        setSelecionadas((s) => alternarSelecaoAcoplada(s, tipo, tiposPendentes))
    const marcarTodas = () => setSelecionadas((s) => alternarTodas(tiposPendentes, s))
    // Além de exigir ao menos 1 marcada, a ficha CPF trava o botão enquanto houver campo
    // OBRIGATÓRIO vazio no mini-form (helper text aponta o que falta).
    // Mostrado/contado = seleção + SINTEGRA incluído pelo backend sob a ESTADUAL.
    const exibidas = comImplicitos(selecionadas, tiposElegiveis)
    const emitDesabilitado = solicitando || !podeEmitir(exibidas.size) || faltantes.length > 0
    // Detecta a falha do background: estávamos CONSULTANDO e a ficha recarregada voltou
    // para NAO_SOLICITADAS (o backend devolve o botão) → avisa e permite tentar de novo.
    const estavaConsultando = useRef(false)
    useEffect(() => {
        if (estavaConsultando.current && status === 'NAO_SOLICITADAS') {
            setErroSolicitar('Não foi possível emitir as certidões agora — tente novamente.')
        }
        estavaConsultando.current = status === 'CONSULTANDO'
    }, [status])
    const solicitar = async () => {
        const tipos = Array.from(selecionadas)
        // Guarda extra: o botão já fica desabilitado sem seleção ou com exigência vazia.
        if (!tipos.length || faltantes.length) return
        setErroSolicitar(null)
        setSolicitando(true)
        try {
            // Ficha CPF: manda junto os dados do titular preenchidos (só os campos dos
            // tipos marcados). Sem nada preenchido (ou ficha PJ), a chamada segue com a
            // aridade antiga — body sem `dados_pf`.
            const dados = ehCpf ? montarDadosPf(selecionadas, dadosPf) : undefined
            await (dados ? onSolicitar(tipos, dados) : onSolicitar(tipos))
            setSelecionadas(new Set()) // limpa a seleção ao disparar o lote
        } catch (e: any) {
            // 409 nunca chega aqui (vira estado informativo no pai); 429/cooldown e
            // demais trazem a mensagem pronta do backend.
            setErroSolicitar(e?.message || 'Não foi possível iniciar a emissão — tente novamente.')
        } finally {
            setSolicitando(false)
        }
    }
    // Vigência (mesmo padrão da SecaoSancoes): consulta reaberta depois pode ter certidão
    // com validade vencida — aí o semáforo vira cinza "Vencida — reemitir". Estado do chip
    // (inclui os cadastros de inscrição) centralizado em `uiCertidao`. `hoje` é calculado no
    // topo do componente (reusado pela guarda de elegibilidade).
    const nomeCertidao = (c: Certidao) =>
        c.tipo === 'ESTADUAL'
            ? `CND Estadual (SEFAZ${radar.uf ? '-' + radar.uf : ''})`
            : CERTIDAO_NOME[c.tipo] || c.tipo
    const [baixandoCert, setBaixandoCert] = useState<string | null>(null)
    const [erroCert, setErroCert] = useState<{tipo: string; msg: string} | null>(null)
    // Download da CÓPIA PERMANENTE no S3 (o recibo do provedor expira em 7 dias —
    // medido 15/10/2026). Mesmo padrão de blob do TCU: o <a href> puro não carrega o JWT.
    const baixarPdfCertidao = async (tipo: string) => {
        if (!consultaId || baixandoCert) return
        setErroCert(null)
        setBaixandoCert(tipo)
        try {
            const blob = await api.baixarCertidaoPdf(consultaId, tipo)
            baixarArquivo(blob, `certidao-${tipo.toLowerCase().replace(/_/g, '-')}.pdf`)
        } catch (e: any) {
            // Só o 404 (cópia S3 ausente) justifica a reemissão — que é PAGA. 503/rede/demais
            // são transitórios no backend: pedir "tente novamente", sem empurrar p/ reemitir.
            setErroCert({
                tipo,
                msg:
                    e?.response?.status === 404
                        ? 'A cópia do PDF não está disponível — reemita a certidão.'
                        : 'Não foi possível baixar o PDF agora — tente novamente mais tarde.',
            })
        } finally {
            setBaixandoCert(null)
        }
    }
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Certidões de regularidade (CNDs)
                    {certidoes.length > 0 && <span style={countStyle}> ({certidoes.length})</span>}
                </h3>
                {mostrarControles && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer'}}>
                            <input
                                type="checkbox"
                                checked={todasMarcadas}
                                onChange={marcarTodas}
                                aria-label="Selecionar todas as certidões"
                                style={{cursor: 'pointer'}}
                            />
                            Selecionar todas
                        </label>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={solicitar}
                            disabled={emitDesabilitado}
                            style={{
                                background: emitDesabilitado ? undefined : VERDE_ESCURO,
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            {solicitando ? (
                                <>
                                    <CircularProgress size={14} style={{marginRight: 8, color: '#fff'}} />
                                    Iniciando…
                                </>
                            ) : (
                                rotuloEmitir(exibidas.size)
                            )}
                        </Button>
                    </div>
                )}
            </div>
            <div style={{fontSize: 13, color: '#64748b', margin: '6px 0 12px'}}>
                Regularidade fiscal e trabalhista exigida na habilitação (arts. 62 a 68 da Lei
                14.133).
                {mostrarControles &&
                    ' Marque as certidões que deseja emitir e clique em "Emitir certidões" — a emissão é feita diretamente nos órgãos oficiais.'}
                {/* O "TCU já checado" é fato da ficha PJ; na CPF o TCU_CONSOLIDADA chega
                    NAO_SOLICITADA como as demais. */}
                {mostrarControles &&
                    !ehCpf &&
                    ' Os cadastros do TCU já foram checados e alimentam o veredito da ficha.'}
                {/* A nota só faz sentido quando as linhas existem (fora do fallback). */}
                {ehCpf &&
                    certidoes.length > 0 &&
                    ' A quitação eleitoral (TSE) e as certidões judiciais estaduais não são emitidas pelo app — essas linhas trazem a orientação e o link oficial.'}
            </div>
            {/* Mini-form de dados do titular (SÓ ficha CPF): aparece conforme os checkboxes
                marcados — CPF_SITUACAO exige data de nascimento; ANTECEDENTES exige nome,
                data e nome da mãe; MPT exige a UF de domicílio; os demais campos são
                opcionais e só refinam a busca do órgão. */}
            {mostrarControles && camposForm.length > 0 && (
                <div
                    style={{
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        borderRadius: 10,
                        padding: '12px 14px',
                        marginBottom: 12,
                    }}
                >
                    <div style={{fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10}}>
                        Dados do titular para a emissão
                    </div>
                    <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                        {camposForm.map(({campo, exigido}) => {
                            const cfg = CAMPO_PF_UI[campo]
                            const valor = dadosPf[campo] || ''
                            // `key` vai direto no JSX (React não aceita key via spread).
                            const comum = {
                                label: cfg.label,
                                size: 'small' as const,
                                required: exigido,
                                value: valor,
                                error: exigido && !valor.trim(),
                                helperText: motivoCampoPf(campo, selecionadas),
                                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                    setDadosPf((d) => ({...d, [campo]: e.target.value})),
                                style: {width: cfg.largura, background: '#fff'},
                            }
                            // UFs são select; a data usa o input nativo (value 'aaaa-mm-dd',
                            // exatamente o formato que o backend espera — sem reformatar).
                            if (campo === 'uf' || campo === 'uf_nascimento')
                                return (
                                    <TextField key={campo} {...comum} select>
                                        {UFS.map((uf) => (
                                            <MenuItem key={uf} value={uf}>
                                                {uf}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )
                            if (campo === 'data_nascimento')
                                return (
                                    <TextField
                                        key={campo}
                                        {...comum}
                                        type="date"
                                        InputLabelProps={{shrink: true}}
                                    />
                                )
                            return <TextField key={campo} {...comum} />
                        })}
                    </div>
                    {faltantes.length > 0 && (
                        <div style={{fontSize: 12, color: '#b45309', marginTop: 8}}>
                            Para habilitar "Emitir certidões", preencha:{' '}
                            {faltantes.map((f) => CAMPO_PF_UI[f].label).join(', ')}.
                        </div>
                    )}
                    <div style={fonteNota}>
                        Usados somente para a emissão junto aos órgãos oficiais.
                    </div>
                </div>
            )}
            {status === 'CONSULTANDO' && (
                <div style={{...avisoHomonimo, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 8}}>
                    <CircularProgress size={14} style={{color: '#1d4ed8'}} />
                    Emitindo certidões nos órgãos oficiais — pode levar alguns minutos. Pode
                    sair e voltar; o andamento continua e a ficha se atualiza sozinha.
                </div>
            )}
            {erroSolicitar && status !== 'CONSULTANDO' && (
                <div style={avisoHomonimo}>⚠️ {erroSolicitar}</div>
            )}
            {radar.certidoes_verificadas === false && status === 'CONCLUIDAS' && (
                <div style={avisoHomonimo}>
                    ⚠️ Consulta automática indisponível — use os links oficiais para emitir as
                    certidões.
                </div>
            )}
            {certidoes.length ? (
                <>
                    {certidoes.map((c: Certidao, i) => {
                        // Estado do chip: municipal manual, vencida, cadastro de inscrição
                        // localizado ou situação padrão (ver `uiCertidao`). Inscrição (SINTEGRA
                        // / municipal) é CADASTRO — nunca "Regular"/"débitos".
                        // Emitível = qualquer linha com rota automática (reemissão liberada
                        // 15/10/2026 — o recibo do provedor expira em 7 dias); só a municipal
                        // manual e as linhas só-orientação da ficha CPF (TSE / Justiça
                        // Estadual) ficam sem checkbox — `uiCertidao` já devolve o chip
                        // "Emissão manual" delas.
                        const emitivel = ehEmitivel(c, hoje, ehCpf)
                        const ui = uiCertidao(c, hoje, ehCpf)
                        const vencida = ui.vencida
                        // Enquanto o lote corre, a linha emitível não pode parecer "Emitir sob
                        // demanda"/"Vencida — reemitir": vira "Emitindo…". Preciso quando
                        // sabemos os tipos (sessão viva); reaberto (emitindoTipos=null) → todas.
                        const emitindoEssa =
                            status === 'CONSULTANDO' &&
                            emitivel &&
                            (!emitindoTipos || emitindoTipos.includes(c.tipo))
                        // Pré-requisito de outra linha marcada (SINTEGRA sob a ESTADUAL): fica
                        // marcado e TRAVADO, com a explicação ao lado do nome.
                        const travado = selecaoTravada(c.tipo, selecionadas)
                        return (
                            <div
                                key={i}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                    flexWrap: 'wrap',
                                    padding: '8px 0',
                                    borderTop: i ? '1px solid #f1f5f9' : 'none',
                                }}
                            >
                                <div style={{display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 240}}>
                                    {mostrarControles && emitivel && (
                                        <input
                                            type="checkbox"
                                            checked={exibidas.has(c.tipo)}
                                            onChange={() => marcarUma(c.tipo)}
                                            // Pré-requisito de outra certidão marcada (SINTEGRA
                                            // com a ESTADUAL): não pode ser desmarcado — o
                                            // backend o re-adicionaria e o contador mentiria.
                                            disabled={travado}
                                            aria-label={`Selecionar ${nomeCertidao(c)}`}
                                            title={travado ? MOTIVO_TRAVA_SINTEGRA : undefined}
                                            style={{marginTop: 3, cursor: travado ? 'not-allowed' : 'pointer', flexShrink: 0}}
                                        />
                                    )}
                                    {/* Já emitida/vigente (sem checkbox): espaçador p/ alinhar
                                        o texto com as linhas emitíveis. */}
                                    {mostrarControles && !emitivel && (
                                        <span aria-hidden="true" style={{width: 13, flexShrink: 0}} />
                                    )}
                                    <div style={{fontSize: 14, flex: 1}}>
                                    {emitindoEssa ? '⏳' : ui.emoji}{' '}
                                    <b style={{color: '#0f172a'}}>{nomeCertidao(c)}</b>{' '}
                                    {/* Consulta SENSÍVEL/informativa (antecedentes, mandados):
                                        badge + microcopy LGPD abaixo — não integra a
                                        habilitação nem muda o veredito. */}
                                    {ehSensivel(c.tipo) && (
                                        <>
                                            <span style={chipInformativo}>Informativo</span>{' '}
                                        </>
                                    )}
                                    <span style={{color: emitindoEssa ? '#1d4ed8' : ui.cor, fontWeight: 600}}>
                                        {/* Retentativa não é emissão nova: o rótulo diz isso
                                            também enquanto o lote corre. */}
                                        {emitindoEssa
                                            ? ui.retry
                                                ? 'Tentando novamente…'
                                                : 'Emitindo…'
                                            : ui.rotulo}
                                    </span>
                                    {!emitindoEssa && ui.nota && (
                                        <span style={{color: '#64748b', fontSize: 12}}> ({ui.nota})</span>
                                    )}
                                    {travado && !emitindoEssa && (
                                        <span style={{color: '#64748b', fontSize: 12}}>
                                            {' '}· {MOTIVO_TRAVA_SINTEGRA}
                                        </span>
                                    )}
                                    {ehInscricao(c.tipo) && c.inscricao && (
                                        <span style={{color: '#0f172a', fontSize: 13}}>
                                            {' '}· nº <b>{c.inscricao}</b>
                                        </span>
                                    )}
                                    {(c.emitida_em || c.validade) && (
                                        <span style={{color: '#94a3b8', fontSize: 13}}>
                                            {c.emitida_em ? ` · emitida em ${fmtData(c.emitida_em)}` : ''}
                                            {c.validade
                                                ? ` · ${vencida ? 'venceu em' : 'válida até'} ${fmtData(c.validade)}`
                                                : ''}
                                        </span>
                                    )}
                                    {c.detalhe && (
                                        <div style={{fontSize: 12, color: '#64748b', marginTop: 2}}>{c.detalhe}</div>
                                    )}
                                    {ehSensivel(c.tipo) && (
                                        <div style={{fontSize: 11, color: '#94a3b8', marginTop: 2}}>
                                            {MICROCOPY_SENSIVEL}
                                        </div>
                                    )}
                                    {erroCert && erroCert.tipo === c.tipo && (
                                        <div style={{fontSize: 12, color: '#b91c1c', marginTop: 2}}>{erroCert.msg}</div>
                                    )}
                                    </div>
                                </div>
                                {(c.pdf_s3_key || c.link_pdf || c.link_emissao_manual) && (
                                    <div style={{fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6}}>
                                        {c.pdf_s3_key && consultaId ? (
                                            <>
                                                <Button
                                                    onClick={() => baixarPdfCertidao(c.tipo)}
                                                    disabled={baixandoCert === c.tipo}
                                                    variant="text"
                                                    size="small"
                                                    style={{textTransform: 'none', color: VERDE_ESCURO, fontWeight: 600, minWidth: 0, padding: '0 4px'}}
                                                >
                                                    {baixandoCert === c.tipo ? 'Baixando…' : 'Ver PDF ↗'}
                                                </Button>
                                                {c.link_emissao_manual ? <span style={{color: '#94a3b8'}}>·</span> : null}
                                            </>
                                        ) : c.link_pdf && !reciboExpirado(c, hoje) ? (
                                            <>
                                                <a href={c.link_pdf} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                                    Ver PDF ↗
                                                </a>
                                                {c.link_emissao_manual ? <> · </> : null}
                                            </>
                                        ) : c.link_pdf ? (
                                            <>
                                                <span style={{color: '#94a3b8', fontSize: 12}} title="O comprovante do provedor expira em 7 dias; a certidão pode seguir válida — reemita para gerar novo PDF.">
                                                    comprovante expirado — reemita
                                                </span>
                                                {c.link_emissao_manual ? <> · </> : null}
                                            </>
                                        ) : null}
                                        {c.link_emissao_manual && (
                                            <a
                                                href={c.link_emissao_manual}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={linkStyle}
                                            >
                                                Emitir no site oficial ↗
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    <div style={fonteNota}>
                        Certidão positiva nem sempre impede a empresa de licitar ou contratar: a
                        regularidade é aferida na fase de habilitação (art. 63, §1º, da Lei
                        14.133), e ME/EPP têm prazo para regularizar (LC 123, art. 43, §1º).
                        Confirme sempre a via oficial.
                    </div>
                </>
            ) : (
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10}}>
                    {/* Ficha CPF: o fallback lista só o que existe para pessoa física
                        (FGTS/CRF é do empregador PJ — sai). */}
                    {CERTIDOES.filter((c) => !ehCpf || !c.somentePj).map((c) => (
                        <a
                            key={c.nome}
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{display: 'block', padding: '12px 14px', border: `1px solid ${VERDE}`, borderRadius: 10, textDecoration: 'none'}}
                        >
                            <div style={{fontWeight: 700, color: VERDE_ESCURO}}>{c.nome} ↗</div>
                            <div style={{fontSize: 12, color: '#64748b'}}>{c.desc}</div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    )
}

function baixarContratosCSV(contratos: ContratacaoFornecedor[], nome: string) {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const linhas = [
        ['Data', 'Órgão', 'UF', 'Objeto', 'Valor', 'Link PNCP'].join(';'),
        ...contratos.map((c) =>
            [c.data || '', c.orgao_nome || '', c.uf || '', c.objeto || '', c.valor ?? '', c.link || ''].map(esc).join(';')
        ),
    ].join('\r\n')
    const blob = new Blob(['﻿' + linhas], {type: 'text/csv;charset=utf-8'}) // BOM → Excel lê UTF-8
    baixarArquivo(blob, `contratos-${(nome || 'fornecedor').slice(0, 40).replace(/[^\w\- ]/g, '')}.csv`)
}

// Dispara o download de um Blob no navegador (âncora temporária + revoke da object URL).
function baixarArquivo(blob: Blob, nome: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = nome
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

// Chip de origem por linha: PNCP (fonte primária) ou Portal da Transparência.
function ChipOrigem({origem}: {origem?: string}) {
    const pt = origem === 'TRANSPARENCIA'
    return (
        <span
            title={pt ? 'Portal da Transparência — Executivo federal' : 'PNCP'}
            style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 8px',
                borderRadius: 999,
                whiteSpace: 'nowrap',
                background: pt ? '#eff6ff' : '#ecfdf5',
                color: pt ? '#1d4ed8' : VERDE_ESCURO,
                border: `1px solid ${pt ? '#bfdbfe' : VERDE}`,
            }}
        >
            {pt ? 'Transparência' : 'PNCP'}
        </span>
    )
}

function SecaoContratos({radar}: {radar: RadarFornecedor}) {
    const [modo, setModo] = useState<'orgao' | 'lista'>('orgao')
    const contratos = radar.contratos || []
    // Contratações federais com vigência anterior à janela: fora dos totais, mas nunca
    // somem em silêncio — contador com link do Portal da Transparência.
    const anteriores = radar.contratos_anteriores_ao_periodo || 0

    const grupos = useMemo(() => {
        const map = new Map<string, {orgao: string; uf?: string; contratos: ContratacaoFornecedor[]; total: number}>()
        for (const c of contratos) {
            const key = c.orgao_cnpj || c.orgao_nome || '—'
            let g = map.get(key)
            if (!g) {
                g = {orgao: c.orgao_nome || '—', uf: c.uf || undefined, contratos: [], total: 0}
                map.set(key, g)
            }
            g.contratos.push(c)
            g.total += c.valor || 0
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total)
    }, [contratos])

    // Mesmo sem contratos NA JANELA, a seção aparece quando há anteriores (visibilidade).
    if (contratos.length === 0 && anteriores === 0) return null

    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Contratos{' '}
                    <span style={{color: '#94a3b8', fontSize: 14}}>
                        ({contratos.length}) · últimos 5 anos
                    </span>
                </h3>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                    <Button
                        onClick={() => baixarContratosCSV(contratos, radar.razao_social || radar.cnpj)}
                        variant="text"
                        size="small"
                        style={{textTransform: 'none', color: VERDE_ESCURO, minWidth: 0}}
                    >
                        ⬇ CSV
                    </Button>
                    <div style={{display: 'flex', border: `1px solid ${VERDE}`, borderRadius: 999, overflow: 'hidden'}}>
                        <Aba ativo={modo === 'orgao'} onClick={() => setModo('orgao')}>Por órgão</Aba>
                        <Aba ativo={modo === 'lista'} onClick={() => setModo('lista')}>Lista completa</Aba>
                    </div>
                </div>
            </div>
            {radar.contratos_fallback_nome && (
                <div style={avisoHomonimo}>
                    ⚠️ Contratos localizados pela <b>razão social</b> e confirmados pelo
                    CNPJ/radical — a busca direta pelo CNPJ não retornou registros no PNCP.
                </div>
            )}
            {contratos.length > 0 &&
                (modo === 'orgao' ? (
                    <>
                        <div style={{fontSize: 13, color: '#94a3b8', marginBottom: 8}}>
                            Clique num órgão para ver objeto e valor de cada contrato.
                        </div>
                        {grupos.map((g, i) => (
                            <OrgaoExpandivel key={i} grupo={g} />
                        ))}
                    </>
                ) : (
                    <ListaContratos contratos={contratos} />
                ))}
            {anteriores > 0 && (
                <div
                    style={{
                        fontSize: 13,
                        color: '#475569',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '8px 12px',
                        marginTop: 10,
                    }}
                >
                    🗄️ {anteriores} contratação(ões) federais anteriores aos últimos 5 anos —{' '}
                    <a
                        href={linkTransparencia(radar.cnpj)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={linkStyle}
                    >
                        ver no Portal da Transparência ↗
                    </a>{' '}
                    <span style={{color: '#94a3b8'}}>(fora dos totais da ficha)</span>
                </div>
            )}
            <div style={fonteNota}>
                Fontes: PNCP + Portal da Transparência (Executivo federal).
                {radar.contratos_transparencia_verificados === false &&
                    ' Portal da Transparência não verificado nesta consulta.'}
            </div>
        </div>
    )
}

function Aba({ativo, onClick, children}: {ativo: boolean; onClick: () => void; children: ReactNode}) {
    return (
        <button
            onClick={onClick}
            aria-pressed={ativo}
            style={{
                ...botaoBase,
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 700,
                background: ativo ? VERDE_ESCURO : 'transparent',
                color: ativo ? '#fff' : VERDE_ESCURO,
            }}
        >
            {children}
        </button>
    )
}

function OrgaoExpandivel({grupo}: {grupo: {orgao: string; uf?: string; contratos: ContratacaoFornecedor[]; total: number}}) {
    const [aberto, setAberto] = useState(false)
    return (
        <div style={{border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, overflow: 'hidden'}}>
            <button
                onClick={() => setAberto((a) => !a)}
                aria-expanded={aberto}
                style={{
                    ...botaoBase,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 14px',
                    background: aberto ? '#f8fafc' : '#fff',
                }}
            >
                <span style={{fontWeight: 600, color: '#0f172a', textAlign: 'left'}}>
                    <span style={{color: VERDE_ESCURO, marginRight: 8}}>{aberto ? '▾' : '▸'}</span>
                    {grupo.orgao}
                    {grupo.uf ? <span style={{color: '#94a3b8'}}> · {grupo.uf}</span> : ''}
                </span>
                <span style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                    <b style={{color: '#0f172a'}}>{brl(grupo.total)}</b>{' '}
                    <span style={{color: '#94a3b8', fontSize: 13}}>· {grupo.contratos.length} contrato(s)</span>
                </span>
            </button>
            <Collapse in={aberto}>
                <div style={{padding: '4px 14px 12px'}}>
                    {grupo.contratos.map((c, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 12,
                                padding: '8px 0',
                                borderTop: '1px solid #f1f5f9',
                                fontSize: 13,
                            }}
                        >
                            <div style={{color: '#475569'}}>
                                <span style={{color: '#94a3b8'}}>
                                    {fmtData(c.data)}
                                    {c.fim_vigencia ? ` (vigência até ${fmtData(c.fim_vigencia)})` : ''}
                                </span>{' '}
                                <ChipOrigem origem={c.origem} /> {c.objeto || 'Objeto não informado'}
                                {c.periodo_nao_confirmado && (
                                    <span style={{color: '#b45309', fontSize: 12}}> · período não confirmado</span>
                                )}
                                {c.estabelecimento && (
                                    <span style={{color: '#94a3b8'}}> · {c.estabelecimento}</span>
                                )}
                                {c.link && (
                                    <>
                                        {' '}
                                        <a href={c.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            {c.origem === 'TRANSPARENCIA' ? 'ver no Portal ↗' : 'ver no PNCP ↗'}
                                        </a>
                                    </>
                                )}
                                {c.link_transparencia && (
                                    <>
                                        {' '}
                                        <a
                                            href={c.link_transparencia}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={linkStyle}
                                        >
                                            ver no Portal da Transparência ↗
                                        </a>
                                    </>
                                )}
                                {c.possivel_duplicidade && (
                                    <div style={{fontSize: 12, color: '#92400e', marginTop: 2}}>
                                        ⚠️ {c.possivel_duplicidade}
                                    </div>
                                )}
                            </div>
                            <b style={{whiteSpace: 'nowrap', color: '#0f172a'}}>{brl(c.valor)}</b>
                        </div>
                    ))}
                </div>
            </Collapse>
        </div>
    )
}

function ListaContratos({contratos}: {contratos: ContratacaoFornecedor[]}) {
    const [pagina, setPagina] = useState(1)
    const [ordem, setOrdem] = useState<'data' | 'valor' | 'orgao'>('data')
    const [busca, setBusca] = useState('')

    const ordenados = useMemo(() => {
        const termo = busca.trim().toLowerCase()
        let arr = [...contratos]
        if (termo)
            arr = arr.filter((c) => `${c.orgao_nome || ''} ${c.objeto || ''}`.toLowerCase().includes(termo))
        if (ordem === 'valor') arr.sort((a, b) => (b.valor || 0) - (a.valor || 0))
        else if (ordem === 'orgao')
            arr.sort((a, b) => (a.orgao_nome || '').localeCompare(b.orgao_nome || '', 'pt-BR'))
        else arr.sort((a, b) => (b.data || '').localeCompare(a.data || ''))
        return arr
    }, [contratos, ordem, busca])

    const totalPaginas = Math.max(1, Math.ceil(ordenados.length / POR_PAGINA))
    const paginaSegura = Math.min(pagina, totalPaginas)
    const inicio = (paginaSegura - 1) * POR_PAGINA
    const itens = ordenados.slice(inicio, inicio + POR_PAGINA)
    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
                <TextField
                    size="small"
                    placeholder="Filtrar por órgão ou objeto…"
                    value={busca}
                    onChange={(e) => {
                        setBusca(e.target.value)
                        setPagina(1)
                    }}
                    style={{flex: 1, minWidth: 200}}
                />
                <TextField
                    select
                    size="small"
                    label="Ordenar por"
                    value={ordem}
                    onChange={(e) => {
                        setOrdem(e.target.value as 'data' | 'valor' | 'orgao')
                        setPagina(1)
                    }}
                    style={{width: 200}}
                >
                    <MenuItem value="data">Data (mais recente)</MenuItem>
                    <MenuItem value="valor">Valor (maior)</MenuItem>
                    <MenuItem value="orgao">Órgão (A–Z)</MenuItem>
                </TextField>
            </div>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
                <thead>
                    <tr style={{textAlign: 'left', color: '#64748b'}}>
                        <th style={th}>Data</th>
                        <th style={th}>Órgão</th>
                        <th style={th}>Objeto</th>
                        <th style={{...th, textAlign: 'right'}}>Valor</th>
                        <th style={th}>Fonte</th>
                    </tr>
                </thead>
                <tbody>
                    {itens.map((c, i) => (
                        <tr key={i}>
                            <td style={{...td, whiteSpace: 'nowrap'}}>
                                {fmtData(c.data)}
                                {c.fim_vigencia && (
                                    <div style={{color: '#94a3b8', fontSize: 12}}>
                                        até {fmtData(c.fim_vigencia)}
                                    </div>
                                )}
                                {c.periodo_nao_confirmado && (
                                    <div style={{color: '#b45309', fontSize: 11}}>
                                        período não confirmado
                                    </div>
                                )}
                            </td>
                            <td style={td}>
                                {c.orgao_nome || '—'}
                                {c.uf ? <span style={{color: '#94a3b8'}}> · {c.uf}</span> : ''}
                                {c.estabelecimento ? (
                                    <span style={{color: '#94a3b8'}}> · {c.estabelecimento}</span>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td style={td}>
                                {c.objeto || '—'}
                                {c.possivel_duplicidade && (
                                    <span title={c.possivel_duplicidade} style={{cursor: 'help'}}>
                                        {' '}
                                        ⚠️
                                    </span>
                                )}
                            </td>
                            <td style={{...td, textAlign: 'right', whiteSpace: 'nowrap'}}>{brl(c.valor)}</td>
                            <td style={{...td, whiteSpace: 'nowrap'}}>
                                <ChipOrigem origem={c.origem} />
                                {c.link && (
                                    <>
                                        {' '}
                                        <a href={c.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            abrir ↗
                                        </a>
                                    </>
                                )}
                                {c.link_transparencia && (
                                    <>
                                        {' '}
                                        <a
                                            href={c.link_transparencia}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={linkStyle}
                                            title="Mesmo contrato no Portal da Transparência"
                                        >
                                            Portal ↗
                                        </a>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {totalPaginas > 1 && (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 16}}>
                    <Button size="small" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        ← Anterior
                    </Button>
                    <span style={{color: '#475569', fontSize: 14}}>
                        Página {pagina} de {totalPaginas}
                    </span>
                    <Button size="small" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        Próxima →
                    </Button>
                </div>
            )}
        </div>
    )
}

function Metric({label, value}: {label: string; value: string}) {
    return (
        <div>
            <div style={{fontSize: 24, fontWeight: 800, color: '#0f172a'}}>{value}</div>
            <div style={{fontSize: 12, color: '#64748b'}}>{label}</div>
        </div>
    )
}

const th: CSSProperties = {padding: 8, borderBottom: '1px solid #e2e8f0'}
const td: CSSProperties = {padding: 8, borderBottom: '1px solid #f1f5f9'}
const countStyle: CSSProperties = {color: '#94a3b8', fontSize: 14, fontWeight: 400}
const avisoHomonimo: CSSProperties = {
    fontSize: 12,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '7px 11px',
    marginBottom: 12,
}
const fonteNota: CSSProperties = {fontSize: 12, color: '#94a3b8', marginTop: 8}
// Badge das consultas sensíveis/informativas (antecedentes, mandados) — mesmo formato do
// ChipOrigem, em azul neutro (informativo, não é alerta).
const chipInformativo: CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    padding: '1px 8px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    verticalAlign: 'middle',
}
const botaoBase: CSSProperties = {border: 'none', cursor: 'pointer', font: 'inherit'}
const botaoLinha: CSSProperties = {
    ...botaoBase,
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    transition: 'all 0.15s',
}
const backBtn: CSSProperties = {
    ...botaoBase,
    marginTop: 18,
    marginBottom: 4,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: `1px solid ${VERDE}`,
    color: VERDE_ESCURO,
    borderRadius: 999,
    padding: '8px 18px',
    fontWeight: 700,
    fontSize: 14,
}
